import { orderFinancialRows } from "./order-financial-summary";
import { RefundAllocationPreview, useRefundPreview } from "./RefundAllocationPreview";
import { RefundDocumentPanel } from "./RefundDocumentPanel";
import { GiftCardPaymentDialog } from "./GiftCardPaymentDialog";
import { GiftCardReplacementFields, giftReplacementInput } from "./GiftCardReplacementFields";
import { RefundGiftCardPrintPanel } from "./RefundGiftCardPrintPanel";
import { CashierOrderTransferDialog } from "./CashierOrderTransferDialog";
import { PreOrderCustomerPicker } from "./PreOrderCustomerPicker";
import { CashierCouponPicker } from "./CashierCouponPicker";
import { CashierPricingRecoveryDialog } from "./CashierPricingRecoveryDialog";
import { receiptDiscountSummary } from "./receipt-discounts";
import { valueError, valueLabel } from "../shared/value-copy";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  SecretField,
  Alert,
  Button,
  Card,
  Checkbox,
  Dialog,
  Drawer,
  EmptyState,
  Field,
  Grid,
  Inline,
  SelectField,
  Spinner,
  Stack,
  StatusBadge,
  TextAreaField,
  useToast
} from "@leylion/design-system";
import {
  addOrderLine,
  applyPreOrderLineReductionOverride,
  applyCashierCoupon,
  removeCashierCoupon,
  reconcileCashierCoupon,
  applyCashierOrderDiscount,
  cancelCashierOrder,
  captureCashierPayment,
  closeCurrentShift,
  correctPaidCashierOrderCustomerData,
  createOrder,
  createPreOrder,
  confirmPreOrder,
  requestCashierRefund,
  createVoidApprovalRequest,
  deleteOrderLine,
  getCashierRefunds,
  getCurrentShiftSummary,
  getFloorPlan,
  generateCashierPreBill,
  getOpenOrderCards,
  getOrderWorkflow,
  getPreOrder,
  searchPreOrders,
  getPosCatalog,
  getRuntimeSettings,
  holdCashierOrder,
  markCashierOrderReady,
  openShift,
  releasePreOrderToKitchen,
  reopenCashierClosedOrder,
  resumeCashierOrder,
  searchCashierCustomers,
  createIdempotencyKey,
  sendCashierOrder,
  updateCashierOrderContext,
  updateOrderLine
} from "../api/client";
import type { ContextOption, ServiceType, Shift } from "../api/types";
import type { PosCatalog, PosProduct } from "../api/catalog-types";
import type { RuntimeSettings } from "../api/settings-types";
import type {
  Customer,
  DiningFloor,
  OpenOrderCard,
  OrderWorkflow,
  PreOrderDetail,
  PaymentMethod,
  PreBill,
  Receipt,
  Refund,
  ShiftSummary
} from "../api/cashier-types";
import { useOfflineQueue } from "../offline/useOfflineQueue";
import { formatMoneyMinor, majorInputToMinor, minorToMajorInput } from "./cashier-money";
import { CashierPresentationV2 } from "./CashierPresentationV2";
import { CashierTopBarV2 } from "./CashierTopBarV2";
import { CashierContextBarV2 } from "./CashierContextBarV2";
import { CashierCatalogV2 } from "./CashierCatalogV2";
import { CashierCartV2 } from "./CashierCartV2";
import { CashierCustomerSearchPicker } from "./CashierCustomerSearchPicker";
import { CashierOpenOrderCard } from "./CashierOpenOrderCard";
import { FutureOrdersDrawer } from "./FutureOrdersDrawer";
import { CashierShiftSummaryV2 } from "./CashierShiftSummaryV2";
import {
  CashierPaymentDialog,
  type PaymentCaptureRequest,
  type SplitPaymentCaptureRequest,
  type SplitPaymentCaptureOutcome
} from "./CashierPaymentDialog";

type Language = "ar" | "en";
type SavedOrderFilter = "All" | ServiceType | "Held" | "Preparation";

type ApprovalSubmissionNotice = {
  operation: "VoidItem" | "Refund";
  requestId?: string;
  expiresAt?: string;
  duplicate?: boolean;
};

type PendingPreOrderReduction = {
  lineId: string;
  lineLabel: string;
  currentQuantityMilli: number;
  targetQuantityMilli: number;
  lineVersion: number;
  modifierOptionIds: string[];
  note: string | null;
  manualUnitPriceMinor: number | null;
  closeConfiguratorOnSuccess: boolean;
};

const serviceTypes: ServiceType[] = ["WalkIn", "DineIn", "Takeaway", "Delivery"];
const serviceTypeLabels: Record<ServiceType, { ar: string; en: string }> = {
  WalkIn: { ar: "استلام", en: "Pickup" },
  DineIn: { ar: "داخلي", en: "Dine-in" },
  Takeaway: { ar: "سفري", en: "Takeaway" },
  Delivery: { ar: "توصيل", en: "Delivery" }
};

function displayOrderNumber(value: string) {
  return /^\d+$/.test(value) ? `#${value}` : value;
}

function escapeHtml(value: string | null | undefined): string {
  return (value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function customerInvoiceAddress(customer: Customer | null | undefined): string {
  const address = customer?.addresses.find((item) => item.isDefault) ?? customer?.addresses[0];
  if (!address) return "";
  return [address.addressLine1, address.addressLine2, address.city, address.region, address.postalCode]
    .filter((value): value is string => Boolean(value?.trim()))
    .join(", ");
}

function openingShiftFallbackSummary(
  opened: Shift,
  context: ContextOption,
  currency: string
): ShiftSummary {
  return {
    shiftId: opened.id,
    status: opened.status,
    currency,
    reportNumber: "OPEN-" + opened.id.replace(/-/g, "").slice(0, 8).toUpperCase(),
    businessName: context.tenantName,
    branchName: context.branchName,
    cashierName: context.roleName,
    openingCashMinor: opened.openingCashMinor,
    cashSalesMinor: 0,
    cashRefundsMinor: 0,
    cashInMinor: 0,
    cashOutMinor: 0,
    creditSalesMinor: 0,
    depositCollectedMinor: 0,
    balanceSettlementCollectedMinor: 0,
    receivableSettlementCollectedMinor: 0,
    preOrderDepositRefundsMinor: 0,
    preOrderBalanceRefundsMinor: 0,
    forfeitedDepositsMinor: 0,
    totalCollectionsMinor: 0,
    netSalesMinor: 0,
    taxCollectedMinor: 0,
    grossSalesMinor: 0,
    refundNetMinor: 0,
    refundTaxMinor: 0,
    totalRefundsMinor: 0,
    netSalesAfterRefundsMinor: 0,
    netTaxAfterRefundsMinor: 0,
    netRevenueMinor: 0,
    expectedCashMinor: opened.openingCashMinor,
    countedCashMinor: opened.closingCashMinor,
    varianceMinor: opened.varianceMinor,
    orderCount: 0,
    paymentCount: 0,
    openedAt: opened.openedAt,
    closedAt: opened.closedAt,
    closingNote: null,
    version: opened.version,
    serviceTypes: [],
    futureOrderCount: 0,
    futureOrderNetMinor: 0,
    futureOrderTaxMinor: 0,
    futureOrderGrossMinor: 0,
    paymentMethods: [],
    movements: []
  };
}

export function CashierWorkspaceV2({
  language,
  context,
  permissions,
  onSessionExpired,
  pendingApprovalCount = 0,
  onOpenManagerApprovals,
  standaloneCashierMode: _standaloneCashierMode = false,
  onToggleLanguage,
  onSwitchContext,
  onLogout
}: {
  language: Language;
  context: ContextOption;
  permissions: string[];
  onSessionExpired: () => void;
  pendingApprovalCount?: number;
  onOpenManagerApprovals?: () => void;
  standaloneCashierMode?: boolean;
  onToggleLanguage?: () => void;
  onSwitchContext?: () => void;
  onLogout?: () => void;
}) {
  const ar = language === "ar";
  const toast = useToast();
  const offline = useOfflineQueue();
  const can = (permission: string) => permissions.includes(permission);
  const isPendingApprovalError = (caught: unknown) =>
    caught instanceof Error && /approval request is already pending/i.test(caught.message);

  const [shift, setShift] = useState<ShiftSummary | null>(null);
  const [catalog, setCatalog] = useState<PosCatalog | null>(null);
  const [runtimeSettings, setRuntimeSettings] = useState<RuntimeSettings | null>(null);
  const [orderCards, setOrderCards] = useState<OpenOrderCard[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [floorPlan, setFloorPlan] = useState<DiningFloor[]>([]);
  const [workflow, setWorkflow] = useState<OrderWorkflow | null>(null);
  const liveWorkflow = useRef(workflow); liveWorkflow.current = workflow;
  const [couponNotice, setCouponNotice] = useState("");
  const [refunds, setRefunds] = useState<Refund[]>([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [approvalNotice, setApprovalNotice] = useState<ApprovalSubmissionNotice | null>(null);

  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [productSearch, setProductSearch] = useState("");

  const [openingShiftDialogOpen, setOpeningShiftDialogOpen] = useState(false);
  const [shiftDrawerOpen, setShiftDrawerOpen] = useState(false);
  const [openingCash, setOpeningCash] = useState("");
  const [countedCash, setCountedCash] = useState("");
  const [closingNote, setClosingNote] = useState("");
  const [closedShiftSummary, setClosedShiftSummary] = useState<ShiftSummary | null>(null);

  const [newOrderOpen, setNewOrderOpen] = useState(false);
  const [newServiceType, setNewServiceType] = useState<ServiceType>("Takeaway");
  const [newOrderNote, setNewOrderNote] = useState("");
  const [newTableId, setNewTableId] = useState("");
  const [newGuestCount, setNewGuestCount] = useState("1");
  const [newDeliveryCustomerId, setNewDeliveryCustomerId] = useState("");
  const [newDeliveryAddressId, setNewDeliveryAddressId] = useState("");
  const [newOrderTiming, setNewOrderTiming] = useState<"Now" | "Future">("Now");
  const [newFulfillmentAt, setNewFulfillmentAt] = useState("");
  const [newPreparationLeadMinutes, setNewPreparationLeadMinutes] = useState("60");
  const [newFutureContactName, setNewFutureContactName] = useState("");
  const [newFutureCustomer, setNewFutureCustomer] = useState<Customer | null>(null);
  const [newFutureCustomerBusy, setNewFutureCustomerBusy] = useState(false);
  const [newFutureContactPhone, setNewFutureContactPhone] = useState("");
  const [newFutureVehicleNumber, setNewFutureVehicleNumber] = useState("");
  const [newFutureAddressNotes, setNewFutureAddressNotes] = useState("");
  const [newFutureDepositTarget, setNewFutureDepositTarget] = useState("0");
  const [futureOrdersOpen, setFutureOrdersOpen] = useState(false);
  const [activePreOrder, setActivePreOrder] = useState<PreOrderDetail | null>(null);

  const [savedOrdersOpen, setSavedOrdersOpen] = useState(false);
  const [savedOrdersSearch, setSavedOrdersSearch] = useState("");
  const [savedOrdersFilter, setSavedOrdersFilter] = useState<SavedOrderFilter>("All");

  const [orderTransfer, setOrderTransfer] = useState<{ mode: "merge" | "split"; orderId: string } | null>(null);
  const [contextOpen, setContextOpen] = useState(false);
  const [contextError, setContextError] = useState("");
  const [pricingRecoveryOrderId, setPricingRecoveryOrderId] = useState<string | null>(null);
  const [contextServiceType, setContextServiceType] = useState<ServiceType>("Takeaway");
  const [contextCustomerId, setContextCustomerId] = useState("");
  const [contextSelectedCustomer, setContextSelectedCustomer] = useState<Customer | null>(null);
  const [contextTableId, setContextTableId] = useState("");
  const [contextGuestCount, setContextGuestCount] = useState("1");
  const [contextContactName, setContextContactName] = useState("");
  const [contextContactPhone, setContextContactPhone] = useState("");
  const [contextVehicleNumber, setContextVehicleNumber] = useState("");
  const [contextAddressId, setContextAddressId] = useState("");
  const [contextDeliveryFee, setContextDeliveryFee] = useState("0");
  const [contextInstructions, setContextInstructions] = useState("");
  const [contextExternalReference, setContextExternalReference] = useState("");
  const [contextTaxInvoiceRequested, setContextTaxInvoiceRequested] = useState(false);
  const [contextTaxInvoiceCompanyName, setContextTaxInvoiceCompanyName] = useState("");
  const [contextTaxInvoiceTrn, setContextTaxInvoiceTrn] = useState("");
  const [contextTaxInvoiceAddress, setContextTaxInvoiceAddress] = useState("");

  const [configProduct, setConfigProduct] = useState<PosProduct | null>(null);
  const [configVariantId, setConfigVariantId] = useState("");
  const [configQuantity, setConfigQuantity] = useState("1");
  const [configOptionIds, setConfigOptionIds] = useState<string[]>([]);
  const [configNote, setConfigNote] = useState("");
  const [editingLineId, setEditingLineId] = useState<string | null>(null);
  const [editingLineVersion, setEditingLineVersion] = useState<number | null>(null);
  const [pendingPreOrderReduction, setPendingPreOrderReduction] = useState<PendingPreOrderReduction | null>(null);
  const [managerReductionPin, setManagerReductionPin] = useState("");
  const [managerReductionReason, setManagerReductionReason] = useState("");
  const [managerReductionError, setManagerReductionError] = useState("");

  const [paymentOpen, setPaymentOpen] = useState(false);
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [receiptWorkflow, setReceiptWorkflow] = useState<OrderWorkflow | null>(null);
  const [printingPreBillOrderId, setPrintingPreBillOrderId] = useState<string | null>(null);
  const [postSaveDetailsOpen, setPostSaveDetailsOpen] = useState(false);
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [paidCustomerCorrectionOpen, setPaidCustomerCorrectionOpen] = useState(false);
  const [paidCustomerCorrectionReason, setPaidCustomerCorrectionReason] = useState("");
  const [holdOpen, setHoldOpen] = useState(false);
  const [holdReason, setHoldReason] = useState("");
  const [discountOpen, setDiscountOpen] = useState(false);
  const [discountValue, setDiscountValue] = useState("10");
  const [discountReason, setDiscountReason] = useState("");
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponError, setCouponError] = useState("");
  const [couponBusy, setCouponBusy] = useState(false);
  const couponSubmitting = useRef(false);
  const [couponNeedsCustomer, setCouponNeedsCustomer] = useState(false);
  const [couponCustomer, setCouponCustomer] = useState<Customer | null>(null);
  const couponAttempt = useRef<{ body: string; key: string } | null>(null);
  useEffect(() => { setCouponOpen(false); setCouponCode(""); setCouponCustomer(null); setCouponNeedsCustomer(false); couponAttempt.current = null; }, [workflow?.order.id]);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelError, setCancelError] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);
  const cancelBusyRef = useRef(false);
  const [shiftCloseError, setShiftCloseError] = useState("");
  const [giftPaymentOpen, setGiftPaymentOpen] = useState(false);
  const [giftReplacementRequired, setGiftReplacementRequired] = useState(false);
  const [giftNoExpiry, setGiftNoExpiry] = useState(false);
  const [giftExpiry, setGiftExpiry] = useState("");
  const [refundBusy, setRefundBusy] = useState(false);
  const refundBusyRef = useRef(false);
  const [refundPending, setRefundPending] = useState<Parameters<typeof requestCashierRefund> | null>(null);
  const [lastRefundDocument, setLastRefundDocument] = useState<{ orderId: string; refundId: string; refundNumber: string } | null>(null);
  const [lastGiftRefund, setLastGiftRefund] = useState<{ orderId: string; refundId: string } | null>(null);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundReason, setRefundReason] = useState("");
  const [refundQuantities, setRefundQuantities] = useState<Record<string, number>>({});

  const formatMoney = (minor: number, currency?: string) =>
    formatMoneyMinor(
      minor,
      currency ?? workflow?.order.currency ?? catalog?.currency ?? runtimeSettings?.currency ?? "AED",
      ar ? "ar-AE" : "en-AE"
    );

  const variantOptionLabel = (variant: PosProduct["variants"][number]) =>
    `${ar ? variant.nameAr : variant.nameEn} · ${formatMoney(variant.unitPriceMinor, catalog?.currency)}`;

  const paymentMethodLabel = (method: PaymentMethod) => {
    const labels: Record<PaymentMethod, { ar: string; en: string }> = {
      Cash: { ar: "نقدي", en: "Cash" },
      Card: { ar: "بطاقة / شبكة", en: "Card / Terminal" },
      GiftCard: { ar: "بطاقة هدية", en: "Gift card" },
    Wallet: { ar: "محفظة", en: "Wallet" },
      Account: { ar: "حساب آجل", en: "On account" }
    };
    return labels[method][ar ? "ar" : "en"];
  };

  const normalizedTaxInvoiceTrn = () => contextTaxInvoiceTrn.replace(/\D/g, "");

  const taxInvoiceValidationMessage = (): string => {
    if (!contextTaxInvoiceRequested) return "";
    if (!contextTaxInvoiceCompanyName.trim()) {
      return ar ? "اسم الشركة مطلوب لإصدار الفاتورة الضريبية." : "Company legal name is required for the tax invoice.";
    }
    const normalizedTrn = normalizedTaxInvoiceTrn();
    if (normalizedTrn.length !== 15) {
      return ar
        ? "يتكون الرقم الضريبي الإماراتي من 15 رقماً بالضبط."
        : "The UAE tax registration number must contain exactly 15 digits.";
    }
    if (!contextTaxInvoiceAddress.trim()) {
      return ar ? "عنوان الشركة مطلوب لإصدار الفاتورة الضريبية." : "Company address is required for the tax invoice.";
    }
    return "";
  };

  useEffect(() => { setContextError(""); }, [contextOpen, postSaveDetailsOpen, workflow?.order.id]);

  const handleError = (caught: unknown) => {
    const apiError = caught as Error & { status?: number; problem?: { title?: string } };
    const code = apiError?.problem?.title ?? "";
    const localized: Record<string, { ar: string; en: string }> = {
      KITCHEN_STATION_REQUIRED: {
        ar: "تعذر إرسال الطلب لأن الفرع لا يحتوي على محطة تجهيز فعّالة. أكمل إعداد المطبخ والتجهيز ثم أعد المحاولة.",
        en: "The order cannot be sent because this branch has no active preparation station. Complete kitchen setup and try again."
      },
      KDS_DISABLED: {
        ar: "تعذر إرسال الطلب لأن شاشة المطبخ والتجهيز معطلة لهذا الفرع.",
        en: "The order cannot be sent because kitchen preparation is disabled for this branch."
      }
    };
    const known = localized[code];
    const message = known
      ? known[ar ? "ar" : "en"]
      : valueError(caught, language);
    setError(message);
    if (typeof caught === "object" && caught !== null && "status" in caught && Number((caught as { status?: number }).status) === 401) {
      onSessionExpired();
    }
  };

  const refreshReferenceData = async () => {
    const [shiftValue, orderCardItems, catalogValue, runtime, customerItems, floors] = await Promise.all([
      getCurrentShiftSummary(),
      getOpenOrderCards(),
      getPosCatalog(context.branchId),
      getRuntimeSettings(context.branchId),
      can("customers.read") ? searchCashierCustomers() : Promise.resolve([]),
      can("tables.read") ? getFloorPlan() : Promise.resolve([])
    ]);
    setShift(shiftValue);
    setOrderCards(orderCardItems);
    setCatalog(catalogValue);
    setRuntimeSettings(runtime);
    setCustomers(customerItems);
    setFloorPlan(floors);
    setNewServiceType(runtime.defaultServiceType);
    setNewPreparationLeadMinutes(String(runtime.preOrderMinimumLeadMinutes));
    setNewFulfillmentAt((current) => {
      if (current) return current;
      const date = new Date(Date.now() + Math.max(60, runtime.preOrderMinimumLeadMinutes) * 60_000);
      date.setSeconds(0, 0);
      const offset = date.getTimezoneOffset() * 60_000;
      return new Date(date.getTime() - offset).toISOString().slice(0, 16);
    });
    // Reference refreshes must not overwrite the active order context.
    // hydrateContext owns its service type and delivery fee.
    setSelectedCategoryId((current) => catalogValue.categories.some((item) => item.id === current) ? current : "");
  };

  useEffect(() => {
    setBusy(true);
    refreshReferenceData().catch(handleError).finally(() => setBusy(false));
  }, [context.branchId]);

  useEffect(() => {
    if (!runtimeSettings?.preOrdersEnabled || !permissions.includes("preorders.release")) {
      return undefined;
    }

    let disposed = false;
    let running = false;
    const reconcileDueFutureOrders = async () => {
      if (disposed || running) return;
      running = true;
      try {
        const now = Date.now();
        const items = await searchPreOrders({
          from: new Date(now - 86_400_000).toISOString(),
          to: new Date(now + 86_400_000).toISOString()
        });
        const due = items.filter((item) =>
          ["AwaitingDeposit", "Confirmed"].includes(item.status)
          && item.itemCount > 0
          && ["Pending", "Failed"].includes(item.kitchenReleaseState)
          && new Date(item.kitchenReleaseAt).getTime() <= now
        );
        let releasedAny = false;
        for (const item of due) {
          try {
            await releasePreOrderToKitchen(
              item.orderId,
              item.orderVersion,
              item.scheduledOrderVersion,
              "Automatic POS reconciliation after the scheduled preparation horizon.",
              `pos:auto-release:${item.scheduledOrderId}:${item.scheduledOrderVersion}`
            );
            releasedAny = true;
          } catch {
            // The server-side worker is authoritative. A concurrent worker release,
            // optimistic-version race, or a KDS failure is reflected by the next poll.
          }
        }
        if (releasedAny && !disposed) {
          setOrderCards(await getOpenOrderCards());
        }
      } catch {
        // Defense-in-depth only: normal POS work must never be blocked by reconciliation.
      } finally {
        running = false;
      }
    };

    void reconcileDueFutureOrders();
    const timer = window.setInterval(() => void reconcileDueFutureOrders(), 5_000);
    return () => {
      disposed = true;
      window.clearInterval(timer);
    };
  }, [context.branchId, runtimeSettings?.preOrdersEnabled, permissions]);

  const hydrateContext = (value: OrderWorkflow) => {
    setContextServiceType(value.order.serviceType);
    setContextCustomerId(value.order.customerId ?? "");
    setContextSelectedCustomer(value.customer ?? null);
    setContextTableId(value.fulfillment.tableId ?? "");
    setContextGuestCount(String(value.fulfillment.guestCount ?? 1));
    setContextContactName(value.fulfillment.contactName ?? value.customer?.name ?? "");
    setContextContactPhone(value.fulfillment.contactPhone ?? value.customer?.phone ?? "");
    setContextVehicleNumber(value.fulfillment.vehicleNumber ?? "");
    setContextAddressId(value.fulfillment.deliveryAddressId ?? "");
    setContextDeliveryFee(minorToMajorInput(value.order.deliveryFeeMinor, value.order.currency));
    setContextInstructions(value.fulfillment.deliveryInstructions ?? "");
    setContextExternalReference(value.order.externalReference ?? "");
    setContextTaxInvoiceRequested(value.order.taxInvoiceRequested);
    setContextTaxInvoiceCompanyName(value.order.taxInvoiceCompanyName ?? value.customer?.companyName ?? "");
    setContextTaxInvoiceTrn(value.order.taxInvoiceTaxRegistrationNumber ?? value.customer?.taxRegistrationNumber ?? "");
    setContextTaxInvoiceAddress(value.order.taxInvoiceCompanyAddress ?? customerInvoiceAddress(value.customer));
  };

  const selectContextCustomerRecord = (customer: Customer | null) => {
    setContextSelectedCustomer(customer);
    setContextCustomerId(customer?.id ?? "");
    setContextContactName(customer?.name ?? "");
    setContextContactPhone(customer?.phone ?? "");
    setContextAddressId("");

    // Replace rather than merge recipient identity so switching
    // customers cannot leak the previous company's legal details.
    setContextTaxInvoiceCompanyName(customer?.companyName ?? "");
    setContextTaxInvoiceTrn(customer?.taxRegistrationNumber ?? "");
    setContextTaxInvoiceAddress(customerInvoiceAddress(customer));
  };

  const selectContextCustomer = (customerId: string) => {
    selectContextCustomerRecord(customers.find((item) => item.id === customerId) ?? null);
  };

  const clearActiveWorkspace = () => {
    setWorkflow(null);
    setCouponNotice("");
    setActivePreOrder(null);
    setRefunds([]);
    setContextOpen(false);
    setConfigProduct(null);
    setEditingLineId(null);
    setEditingLineVersion(null);
    setProductSearch("");
  };

  const loadWorkflow = async (orderId: string) => {
    setBusy(true);
    setError("");
    try {
      const knownOpenOrder = orderCards.find((item) => item.orderId === orderId);
      const shouldRefreshPreOrder = activePreOrder?.summary.orderId === orderId
        || Boolean(knownOpenOrder?.isPreOrder);
      const [next, refundItems, orderCardItems, refreshedPreOrder] = await Promise.all([
        getOrderWorkflow(orderId),
        can("refunds.create") ? getCashierRefunds(orderId) : Promise.resolve([]),
        getOpenOrderCards(),
        shouldRefreshPreOrder ? getPreOrder(orderId).catch(() => null) : Promise.resolve(null)
      ]);
      setWorkflow(next);
      setActivePreOrder(refreshedPreOrder);
      setRefunds(refundItems);
      setOrderCards(orderCardItems);
      hydrateContext(next);
    } catch (caught) {
      handleError(caught);
    } finally {
      setBusy(false);
    }
  };

  const refreshOperationalCards = async () => {
    const orderCardItems = await getOpenOrderCards();
    setOrderCards(orderCardItems);
  };

  const availableTables = useMemo(
    () => floorPlan.flatMap((floor) => floor.tables.filter((table) => table.status === "Available" || table.currentOrderId === workflow?.order.id)),
    [floorPlan, workflow?.order.id]
  );

  const filteredProducts = useMemo(() => {
    const query = productSearch.trim().toLocaleLowerCase();
    return catalog?.products.filter((product) =>
      (!selectedCategoryId || product.categoryId === selectedCategoryId) &&
      (!query || [product.code, product.nameAr, product.nameEn].some((value) => value.toLocaleLowerCase().includes(query)))
    ) ?? [];
  }, [catalog, selectedCategoryId, productSearch]);

  const visibleSavedOrders = useMemo(() => {
    const query = savedOrdersSearch.trim().toLocaleLowerCase();
    return orderCards.filter((order) => {
      const filterMatch = savedOrdersFilter === "All"
        || (serviceTypes.includes(savedOrdersFilter as ServiceType) && order.serviceType === savedOrdersFilter)
        || (savedOrdersFilter === "Held" && order.status === "Held")
        || (savedOrdersFilter === "Preparation" && ["SentToKitchen", "Ready"].includes(order.status));
      if (!filterMatch) return false;
      if (!query) return true;
      return [order.orderNumber, order.customerName, order.contactName, order.customerPhone, order.contactPhone, order.vehicleNumber]
        .some((value) => value?.toLocaleLowerCase().includes(query));
    });
  }, [orderCards, savedOrdersFilter, savedOrdersSearch]);

  const isClosedWorkflow = Boolean(workflow && ["Completed", "Cancelled", "Refunded"].includes(workflow.order.status));
  const isPreOrderCartLocked = Boolean(
    activePreOrder && workflow?.order.id === activePreOrder.summary.orderId && (
      ["Fulfilled", "Cancelled"].includes(activePreOrder.summary.status)
      || workflow.order.paymentStatus === "Paid"
      || ["Ready", "Completed", "Cancelled", "Refunded"].includes(workflow?.order.status ?? "")
      || ["Ready", "Completed", "Cancelled"].includes(workflow?.order.fulfillmentStatus ?? "")
    )
  );
  const isPreOrderLifecycleManaged = Boolean(activePreOrder && workflow?.order.id === activePreOrder.summary.orderId);
  const paidPreOrderCoupon = Boolean(isPreOrderLifecycleManaged && workflow?.appliedCoupon
    && (workflow.order.amountPaidMinor !== 0 || workflow.payments.length > 0));
  const couponCustomerLocked = Boolean(isPreOrderLifecycleManaged && workflow
    && (workflow.order.amountPaidMinor !== 0 || workflow.payments.length > 0));
  const canSelectCouponCustomer = can("customers.read") && can(isPreOrderLifecycleManaged ? "preorders.update" : "orders.update") && !couponCustomerLocked;
  const canReviewPricing = !isClosedWorkflow && can("orders.update") && can("discounts.apply") && can("discounts.override_limit")
    && (!activePreOrder || (!['Fulfilled', 'Cancelled'].includes(activePreOrder.summary.status) && Boolean(workflow?.appliedCoupon)));
  const requiresPreOrderManagerReduction = Boolean(
    activePreOrder
      && workflow?.order.id === activePreOrder.summary.orderId
      && ["AwaitingDeposit", "Confirmed"].includes(activePreOrder.summary.status)
  );
  const isOrderItemEditingLocked = isClosedWorkflow
    || isPreOrderCartLocked
    || Boolean(activePreOrder && !can("preorders.update"));
  const preOrderReductionHelp = activePreOrder?.summary.status === "Draft"
    ? (ar
      ? "Draft: يمكن تنقيص الكمية أو حذف الصنف بدون موافقة مدير."
      : "Draft: quantity may be reduced without approval")
    : requiresPreOrderManagerReduction
      ? (ar
        ? "بعد التأكيد: يتطلب التنقيص أو الحذف رمز مدير."
        : "After confirmation: requires a manager PIN")
      : undefined;
  const canReopenWorkflow = Boolean(
    workflow
      && ["Completed", "Refunded"].includes(workflow.order.status)
      && ["Paid", "PartiallyRefunded", "Refunded"].includes(workflow.order.paymentStatus)
  );

  const openNewShift = async () => {
    const currency = runtimeSettings?.currency ?? catalog?.currency ?? "AED";
    const openingCashMinor = majorInputToMinor(openingCash, currency);
    if (openingCash.trim() === "" || openingCashMinor === null || openingCashMinor < 0) {
      setError(
        ar
          ? "أدخل الرصيد الافتتاحي الفعلي للدرج. يمكن إدخال 0 عند عدم وجود عهدة."
          : "Enter the actual opening drawer balance. Enter 0 when there is no float."
      );
      return;
    }

    try {
      const opened = await openShift(openingCashMinor);

      // Commit the successful POST response to UI state before any secondary
      // read-only refresh. A transient summary failure must not make the shift
      // opening transaction look failed to the cashier.
      setShift(
        openingShiftFallbackSummary(
          opened,
          context,
          runtimeSettings?.currency ?? catalog?.currency ?? "AED"
        )
      );
      setOpeningShiftDialogOpen(false);
      setOpeningCash("");
      setError("");
      toast.notify({
        tone: "success",
        title: ar ? "تم فتح الوردية" : "Shift opened"
      });

      try {
        const summary = await getCurrentShiftSummary();
        if (summary) {
          setShift(summary);
        }
      } catch (refreshError) {
        console.warn("Shift opened; summary refresh will retry.", refreshError);
      }
    } catch (caught) {
      handleError(caught);
    }
  };

  const createNewOrder = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!offline.online) return;

    const isFutureOrder = newOrderTiming === "Future";
    try {
      if (isFutureOrder) {
        if (!runtimeSettings?.preOrdersEnabled) {
          throw new Error(ar ? "فعّل الطلبات المستقبلية من إعدادات الفرع أولاً." : "Enable future orders in branch settings first.");
        }
        if (!newFulfillmentAt) {
          throw new Error(ar ? "حدد تاريخ ووقت الاستلام." : "Select fulfillment date and time.");
        }
        const depositRequiredMinor = newFutureDepositTarget.trim()
          ? majorInputToMinor(newFutureDepositTarget, runtimeSettings?.currency ?? catalog?.currency ?? "AED")
          : 0;
        if (depositRequiredMinor == null || depositRequiredMinor < 0) {
          throw new Error(ar ? "أدخل مبلغ عربون اختيارياً صالحاً أو صفراً." : "Enter a valid optional deposit amount or zero.");
        }
        if (newFutureCustomerBusy) return;
        const created = await createPreOrder({
          customerId: newFutureCustomer?.id ?? null,
          serviceType: newServiceType,
          note: newOrderNote.trim() || null,
          fulfillmentAt: new Date(newFulfillmentAt).toISOString(),
          preparationLeadMinutes: Number(newPreparationLeadMinutes),
          contactName: newFutureContactName.trim() || null,
          contactPhone: newFutureContactPhone.trim() || null,
          vehicleNumber: newFutureVehicleNumber.trim() || null,
          addressNotes: newFutureAddressNotes.trim() || null,
          depositRequiredMinor
        });
        setActivePreOrder(created);
        setWorkflow(created.workflow);
        hydrateContext(created.workflow);
        setFutureOrdersOpen(false);
        setContextOpen(false);
        setNewOrderOpen(false);
        setNewOrderNote("");
        setNewFutureContactName("");
        setNewFutureCustomer(null);
        setNewFutureContactPhone("");
        setNewFutureVehicleNumber("");
        setNewFutureAddressNotes("");
        setNewFutureDepositTarget("0");
        setNewOrderTiming("Now");
        await refreshReferenceData();
        return;
      }
      if (runtimeSettings?.requireOpenShift !== false && shift?.status !== "Open") {
        throw new Error(ar ? "افتح الوردية أولاً." : "Open the shift first.");
      }
      if (newServiceType === "DineIn" && (!newTableId || Number(newGuestCount) < 1)) {
        throw new Error(ar ? "حدد طاولة وعدد ضيوف صحيحاً." : "Select a table and valid guest count.");
      }
      if (newServiceType === "Delivery" && (!newDeliveryCustomerId || !newDeliveryAddressId)) {
        throw new Error(ar ? "حدد عميل التوصيل وعنوانه." : "Select the delivery customer and address.");
      }

      const created = await createOrder(newServiceType, newOrderNote);
      let createdWorkflow = await getOrderWorkflow(created.id);
      if (newServiceType === "DineIn" || newServiceType === "Delivery") {
        const deliveryCustomer = customers.find((item) => item.id === newDeliveryCustomerId);
        createdWorkflow = await updateCashierOrderContext(createdWorkflow, {
          serviceType: newServiceType,
          customerId: newServiceType === "Delivery" ? newDeliveryCustomerId : null,
          tableId: newServiceType === "DineIn" ? newTableId : null,
          guestCount: newServiceType === "DineIn" ? Number(newGuestCount) : null,
          contactName: newServiceType === "Delivery" ? deliveryCustomer?.name ?? null : null,
          contactPhone: newServiceType === "Delivery" ? deliveryCustomer?.phone ?? null : null,
          vehicleNumber: null,
          deliveryAddressId: newServiceType === "Delivery" ? newDeliveryAddressId : null,
          deliveryFeeMinor: newServiceType === "Delivery" ? runtimeSettings?.defaultDeliveryFeeMinor ?? 0 : 0,
          deliveryInstructions: null,
          externalReference: null,
          promisedAt: null,
          taxInvoiceRequested: false,
          taxInvoiceCompanyName: null,
          taxInvoiceTaxRegistrationNumber: null,
          taxInvoiceCompanyAddress: null
        });
      }
      setActivePreOrder(null);
      setWorkflow(createdWorkflow);
      hydrateContext(createdWorkflow);
      setRefunds([]);
      setNewOrderOpen(false);
      setNewOrderNote("");
      setNewTableId("");
      setNewGuestCount("1");
      setNewDeliveryCustomerId("");
      setNewDeliveryAddressId("");
      setNewFutureContactName("");
      setNewFutureContactPhone("");
      setNewFutureVehicleNumber("");
      setNewFutureAddressNotes("");
      setNewFutureDepositTarget("0");
      setNewOrderTiming("Now");
      await refreshReferenceData();
    } catch (caught) {
      handleError(caught);
    }
  };

  const openProduct = (product: PosProduct) => {
    if (!workflow || isOrderItemEditingLocked) return;
    const variant = product.variants.find((item) => item.isDefault) ?? product.variants[0];
    setConfigProduct(product);
    setConfigVariantId(variant?.id ?? "");
    setConfigQuantity("1");
    setConfigOptionIds(product.modifierGroups.flatMap((group) => group.options.filter((option) => option.isDefault).map((option) => option.id)));
    setConfigNote("");
    setEditingLineId(null);
    setEditingLineVersion(null);
  };

  const openLineEditor = (line: OrderWorkflow["cart"]["lines"][number]) => {
    const product = catalog?.products.find((item) => item.id === line.productId);
    if (!product) return;
    setConfigProduct(product);
    setConfigVariantId(line.productVariantId);
    setConfigQuantity(String(line.quantityMilli / 1000));
    setConfigOptionIds(line.modifiers.map((item) => item.modifierOptionId));
    setConfigNote(line.note ?? "");
    setEditingLineId(line.id);
    setEditingLineVersion(line.version);
  };

  const closeConfigurator = () => {
    setConfigProduct(null);
    setEditingLineId(null);
    setEditingLineVersion(null);
  };

  const saveConfiguredProduct = async () => {
    if (!workflow || !configProduct || !configVariantId) return;
    const quantityMilli = Math.round(Number(configQuantity) * 1000);
    const quantityStepMilli = Math.max(1, configProduct.quantityStepMilli);
    if (!Number.isFinite(quantityMilli) || quantityMilli <= 0) return;
    if (quantityMilli % quantityStepMilli !== 0) {
      setError(configProduct.isFractional
        ? (ar ? "الكمية لا تطابق دقة وحدة القياس لهذا الصنف." : "Quantity does not match this item’s unit precision.")
        : (ar ? "هذا الصنف يقبل وحدات كاملة فقط." : "This item accepts whole units only."));
      return;
    }
    try {
      if (editingLineId && editingLineVersion !== null) {
        const currentLine = workflow.cart.lines.find((line) => line.id === editingLineId);
        const manualUnitPriceMinor = configProduct.allowOpenPrice && currentLine
          ? currentLine.unitPriceMinor
          : null;
        if (requiresPreOrderManagerReduction
          && currentLine
          && quantityMilli < currentLine.quantityMilli) {
          setManagerReductionPin("");
          setManagerReductionReason("");
          setManagerReductionError("");
          setPendingPreOrderReduction({
            lineId: currentLine.id,
            lineLabel: ar ? currentLine.productNameAr : currentLine.productNameEn,
            currentQuantityMilli: currentLine.quantityMilli,
            targetQuantityMilli: quantityMilli,
            lineVersion: editingLineVersion,
            modifierOptionIds: configOptionIds,
            note: configNote.trim() || null,
            manualUnitPriceMinor,
            closeConfiguratorOnSuccess: true
          });
          return;
        }
        await updateOrderLine(
          workflow.order.id,
          editingLineId,
          quantityMilli,
          configOptionIds,
          editingLineVersion,
          workflow.order.version,
          manualUnitPriceMinor,
          configNote.trim() || null
        );
      } else {
        await addOrderLine(
          workflow.order.id,
          configVariantId,
          configOptionIds,
          workflow.order.version,
          null,
          quantityMilli,
          configNote.trim() || null
        );
      }
      closeConfigurator();
      await loadWorkflow(workflow.order.id);
    } catch (caught) {
      handleError(caught);
    }
  };

  const changeLineQuantity = async (line: OrderWorkflow["cart"]["lines"][number], deltaMilli: number) => {
    if (!workflow || isOrderItemEditingLocked) return;
    const quantityMilli = line.quantityMilli + deltaMilli;
    const product = catalog?.products.find((item) => item.id === line.productId);
    const quantityStepMilli = product?.quantityStepMilli ?? 1000;
    const manualUnitPriceMinor = product?.allowOpenPrice ? line.unitPriceMinor : null;
    if (deltaMilli % quantityStepMilli !== 0) return;
    if (requiresPreOrderManagerReduction && quantityMilli < line.quantityMilli) {
      setManagerReductionPin("");
      setManagerReductionReason("");
      setManagerReductionError("");
      setPendingPreOrderReduction({
        lineId: line.id,
        lineLabel: ar ? line.productNameAr : line.productNameEn,
        currentQuantityMilli: line.quantityMilli,
        targetQuantityMilli: Math.max(0, quantityMilli),
        lineVersion: line.version,
        modifierOptionIds: line.modifiers.map((item) => item.modifierOptionId),
        note: line.note,
        manualUnitPriceMinor,
        closeConfiguratorOnSuccess: false
      });
      return;
    }
    try {
      if (quantityMilli <= 0) {
        await deleteOrderLine(workflow.order.id, line.id, workflow.order.version);
      } else {
        await updateOrderLine(
          workflow.order.id,
          line.id,
          quantityMilli,
          line.modifiers.map((item) => item.modifierOptionId),
          line.version,
          workflow.order.version,
          manualUnitPriceMinor,
          line.note
        );
      }
      await loadWorkflow(workflow.order.id);
    } catch (caught) {
      handleError(caught);
    }
  };

  const submitPreOrderManagerReduction = async () => {
    if (!workflow || !pendingPreOrderReduction) return;
    const pin = managerReductionPin.trim();
    const reason = managerReductionReason.trim();
    if (!/^\d{4,8}$/.test(pin)) {
      setManagerReductionError(ar ? "أدخل رمز مدير من 4 إلى 8 أرقام." : "Enter a 4 to 8 digit manager PIN.");
      return;
    }
    if (reason.length < 3) {
      setManagerReductionError(ar ? "سبب التنقيص أو الحذف إلزامي." : "A reduction or removal reason is required.");
      return;
    }

    setManagerReductionError("");
    try {
      await applyPreOrderLineReductionOverride(
        workflow.order.id,
        pendingPreOrderReduction.lineId,
        {
          quantityMilli: pendingPreOrderReduction.targetQuantityMilli,
          modifierOptionIds: pendingPreOrderReduction.modifierOptionIds,
          manualUnitPriceMinor: pendingPreOrderReduction.manualUnitPriceMinor,
          note: pendingPreOrderReduction.note,
          expectedLineVersion: pendingPreOrderReduction.lineVersion,
          expectedOrderVersion: workflow.order.version,
          managerPin: pin,
          reason
        }
      );
      if (pendingPreOrderReduction.closeConfiguratorOnSuccess) closeConfigurator();
      setPendingPreOrderReduction(null);
      setManagerReductionPin("");
      setManagerReductionReason("");
      await loadWorkflow(workflow.order.id);
      toast.notify({ tone: "success", title: ar ? "تم اعتماد تعديل الكمية" : "Quantity change authorized" });
    } catch (caught) {
      setManagerReductionError(caught instanceof Error ? caught.message : "Manager override failed.");
    }
  };

const saveOrderContext = async (showPostSaveDetails = false, clearAfterSave = false) => {
  if (!workflow) return;
  setContextError("");
  const taxInvoiceError = taxInvoiceValidationMessage();
  if (taxInvoiceError) {
    setContextError(taxInvoiceError);
    setError(taxInvoiceError);
    return;
  }
  try {
      const revised = await updateCashierOrderContext(workflow, {
        serviceType: contextServiceType,
        customerId: contextCustomerId || null,
        tableId: contextServiceType === "DineIn" ? contextTableId || null : null,
        guestCount: contextServiceType === "DineIn" ? Number(contextGuestCount) : null,
        contactName: contextContactName.trim() || null,
        contactPhone: contextContactPhone.trim() || null,
        vehicleNumber: contextVehicleNumber.trim() || null,
        deliveryAddressId: contextServiceType === "Delivery" ? contextAddressId || null : null,
        deliveryFeeMinor: contextServiceType === "Delivery"
          ? majorInputToMinor(contextDeliveryFee, workflow.order.currency) ?? 0
          : 0,
        deliveryInstructions: contextServiceType === "Delivery" ? contextInstructions.trim() || null : null,
        externalReference: contextExternalReference.trim() || null,
        promisedAt: null,
        taxInvoiceRequested: contextTaxInvoiceRequested,
        taxInvoiceCompanyName: contextTaxInvoiceRequested ? contextTaxInvoiceCompanyName.trim() || null : null,
        taxInvoiceTaxRegistrationNumber: contextTaxInvoiceRequested ? normalizedTaxInvoiceTrn() || null : null,
        taxInvoiceCompanyAddress: contextTaxInvoiceRequested ? contextTaxInvoiceAddress.trim() || null : null
      });
      setWorkflow(revised);
      hydrateContext(revised);
      setContextOpen(false);
      await refreshReferenceData();
      if (showPostSaveDetails) setPostSaveDetailsOpen(true);
      if (clearAfterSave) {
        setPostSaveDetailsOpen(false);
        clearActiveWorkspace();
      }
    } catch (caught) {
      setContextError(valueError(caught, language));
      handleError(caught);
    }
  };

  const saveCurrentOrder = async () => {
    if (!workflow) return;
    if (isPreOrderLifecycleManaged) {
      try {
        const current = await getPreOrder(workflow.order.id);
        let prepared = current;
        if (current.workflow.cart.lines.length > 0
            && ["Draft", "AwaitingDeposit"].includes(current.summary.status)) {
          prepared = await confirmPreOrder(
            current.summary.orderId,
            current.summary.orderVersion,
            current.summary.scheduledOrderVersion
          );
        }
        setActivePreOrder(prepared);
        await refreshReferenceData();
        clearActiveWorkspace();
        setFutureOrdersOpen(true);
        toast.notify({
          tone: "success",
          title: ar ? "تم حفظ وجدولة الطلب المستقبلي" : "Future order saved and scheduled",
          message: prepared.workflow.cart.lines.length > 0
            ? ar
              ? "أصبح الطلب مؤهلاً للإرسال التلقائي للمطبخ عند موعد التحضير المحدد."
              : "The order is now eligible for automatic kitchen release at its preparation time."
            : ar
              ? "حُفظ الطلب كمسودة. أضف صنفاً واحداً على الأقل ليصبح مؤهلاً للإرسال التلقائي."
              : "The booking remains a draft. Add at least one item to enable automatic kitchen release."
        });
      } catch (caught) {
        handleError(caught);
      }
      return;
    }
    await saveOrderContext(true);
  };

  const holdOrder = async () => {
    if (!workflow || !holdReason.trim()) return;
    try {
      await holdCashierOrder(workflow, holdReason.trim());
      setHoldOpen(false);
      setHoldReason("");
      await refreshReferenceData();
      clearActiveWorkspace();
    } catch (caught) {
      handleError(caught);
    }
  };

  const sendOrder = async () => {
    if (!workflow) return;
    try {
      const sendable = workflow.order.status === "Held"
        ? await resumeCashierOrder(workflow)
        : workflow;
      await sendCashierOrder(sendable);
      await refreshReferenceData();
      clearActiveWorkspace();
    } catch (caught) {
      handleError(caught);
    }
  };

  const resumeOrder = async () => {
    if (!workflow) return;
    try {
      setWorkflow(await resumeCashierOrder(workflow));
      await refreshReferenceData();
    } catch (caught) {
      handleError(caught);
    }
  };

  const markReady = async () => {
    if (!workflow) return;
    try {
      setWorkflow(await markCashierOrderReady(workflow));
      await refreshOperationalCards();
    } catch (caught) {
      handleError(caught);
    }
  };

  const applyDiscount = async () => {
    if (!workflow || !discountReason.trim()) return;
    try {
      setWorkflow(await applyCashierOrderDiscount(
        workflow,
        "Percentage",
        Math.round(Number(discountValue) * 100),
        null,
        discountReason.trim(),
        null
      ));
      setDiscountOpen(false);
      setDiscountReason("");
      await refreshOperationalCards();
    } catch (caught) {
      handleError(caught);
    }
  };

  const couponRemovedText = ar ? "أزيلت القسيمة لأن الجمع مع الخصم اليدوي لم يعد مسموحًا. تم تحديث الإجمالي مع إبقاء الخصم اليدوي." : "The coupon was removed because it no longer combines with the manual discount. Totals were updated; the manual discount remains.";
  const reconcileCoupon = async (base: OrderWorkflow) => {
    if (!base.appliedCoupon || base.order.amountPaidMinor !== 0 || !can("discounts.apply")) return base;
    const updated = await reconcileCashierCoupon(base.order.id);
    return updated;
  };
  useEffect(() => {
    if (!workflow?.appliedCoupon || workflow.order.amountPaidMinor !== 0 || busy || couponBusy || paymentOpen || activePreOrder) return;
    let disposed = false; let running = false;
    const refresh = async () => {
      if (running || document.visibilityState !== "visible") return;
      const base = liveWorkflow.current;
      if (!base || !base.appliedCoupon || base.order.id !== workflow.order.id) return;
      running = true;
      try {
        const updated = await reconcileCoupon(base);
        if (!disposed && liveWorkflow.current?.order.id === base.order.id && liveWorkflow.current.order.version === base.order.version && updated.order.version > base.order.version) {
          if (base.appliedCoupon && !updated.appliedCoupon) setCouponNotice(couponRemovedText);
          setWorkflow(updated);
        }
      } catch (caught) { if (!disposed && liveWorkflow.current?.order.id === base.order.id) setCouponNotice(valueError(caught, language)); }
      finally { running = false; }
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), 5000);
    window.addEventListener("focus", refresh);
    return () => { disposed = true; window.clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, [workflow?.order.id, workflow?.appliedCoupon?.couponId, workflow?.order.amountPaidMinor, busy, couponBusy, paymentOpen, Boolean(activePreOrder), language]);
  const acceptCouponWorkflow = (updated: OrderWorkflow) => {
    setWorkflow(updated);
    // Copy server amounts/status into the active booking summary as well. Coupon
    // changes do not alter the booking's schedule, deposit or kitchen version.
    setActivePreOrder(current => current?.summary.orderId === updated.order.id ? {
      ...current, workflow: updated, summary: { ...current.summary,
        orderVersion: updated.order.version, orderTotalMinor: updated.order.totalMinor,
        remainingBalanceMinor: updated.order.balanceDueMinor, paymentStatus: updated.order.paymentStatus }
    } : current);
  };
  const removeCoupon = async () => {
    if (!workflow || couponSubmitting.current) return;
    couponSubmitting.current = true; setCouponBusy(true); setCouponError("");
    try { acceptCouponWorkflow(await removeCashierCoupon(workflow.order.id, workflow.order.version)); setCouponOpen(false); setCouponCode(""); setCouponNotice(""); }
    catch (caught) { setCouponError(valueError(caught, language)); }
    finally { couponSubmitting.current = false; setCouponBusy(false); }
  };
  const preparePayment = async () => {
    if (!workflow || busy) return;
    setBusy(true);
    try {
      if (!activePreOrder) {
        const updated = await reconcileCoupon(workflow);
        if (liveWorkflow.current?.order.id !== workflow.order.id) return;
        if (workflow.appliedCoupon && !updated.appliedCoupon) setCouponNotice(couponRemovedText);
        setWorkflow(updated);
      }
      activePreOrder ? setFutureOrdersOpen(true) : setPaymentOpen(true);
    } catch (caught) { handleError(caught); }
    finally { setBusy(false); }
  };

  const applyCoupon = async () => {
    if (!workflow || !couponCode.trim() || couponSubmitting.current) return;
    couponSubmitting.current = true;
    setCouponBusy(true);
    setCouponError("");
    try {
      const customerId = !workflow.order.customerId && couponNeedsCustomer ? couponCustomer?.id : undefined;
      const body = JSON.stringify([workflow.order.id, couponCode.trim(), workflow.order.version, customerId]);
      if (couponAttempt.current?.body !== body) couponAttempt.current = { body, key: createIdempotencyKey() };
      const updated = await applyCashierCoupon(workflow.order.id, couponCode.trim(), workflow.order.version, customerId, couponAttempt.current.key);
      couponAttempt.current = null;
      if (liveWorkflow.current?.order.id !== updated.order.id) return;
      acceptCouponWorkflow(updated);
      setCouponNeedsCustomer(false); setCouponCustomer(null);
      setCouponOpen(false);
      setCouponCode("");
      setCouponNotice("");
    } catch (caught) {
      const failure = caught as { status?: number; problem?: { title?: string } };
      if (failure.status && failure.status >= 400 && failure.status < 500) couponAttempt.current = null;
      if (failure.problem?.title === "COUPON_CUSTOMER_REQUIRED") setCouponNeedsCustomer(true);
      setCouponError(valueError(caught, language));
      if ((caught as { status?: number })?.status === 401) onSessionExpired();
      return;
    } finally {
      couponSubmitting.current = false;
      setCouponBusy(false);
    }
    try { await refreshOperationalCards(); } catch (caught) { handleError(caught); }
  };

  const cancelOrder = async () => {
    if (!workflow || cancelBusyRef.current) return;

    if (isPreOrderLifecycleManaged) {
      setCancelOpen(false);
      setFutureOrdersOpen(true);
      return;
    }

    const hasItems = workflow.cart.lines.length > 0;
    // Match OrderStateRules: SentToKitchen can be cancelled; Ready requires
    // the existing protected Manager Void approval and execution path.
    const requiresManagerApproval = workflow.order.status === "Ready";
    const reason = hasItems
      ? cancelReason.trim()
      : (ar ? "إلغاء طلب محفوظ بدون أصناف" : "Cancelled empty saved order");

    if (hasItems && !reason) return;

    cancelBusyRef.current = true; setCancelBusy(true); setCancelError("");
    try {
      if (requiresManagerApproval) {
        const approvalRequest = await createVoidApprovalRequest(workflow, reason);
        setCancelOpen(false);
        setCancelReason("");
        setApprovalNotice({
          operation: "VoidItem",
          requestId: approvalRequest.id,
          expiresAt: approvalRequest.expiresAt
        });
        setError("");
        toast.notify({
          tone: "success",
          title: ar ? "تم إرسال طلب الإلغاء للمدير" : "Cancellation approval submitted",
          message: ar
            ? "الطلب جاهز؛ يجب مراجعته واعتماده وتنفيذه من موافقات المدير وفق الصلاحيات المعتمدة."
            : "The order is Ready. Review, approve and execute it in Manager Approvals using the required permissions."
        });
        return;
      }

      await cancelCashierOrder(workflow, reason);
      setCancelOpen(false);
      setCancelReason("");
      await refreshReferenceData();
      clearActiveWorkspace();
      toast.notify({ tone: "success", title: ar ? "تم إلغاء الطلب" : "Order cancelled" });
    } catch (caught) {
      if (isPendingApprovalError(caught)) {
        setCancelOpen(false);
        setCancelReason("");
        setApprovalNotice({ operation: "VoidItem", duplicate: true });
        setError("");
        toast.notify({
          tone: "warning",
          title: ar ? "الطلب بانتظار موافقة المدير" : "Manager approval already pending",
          message: ar
            ? "لم يتم إنشاء طلب مكرر. افتح شاشة موافقات المدير بحساب مخول."
            : "No duplicate was created. Open Manager Approvals with an authorized account."
        });
        return;
      }
      setCancelError(valueError(caught, language));
      handleError(caught);
    } finally { cancelBusyRef.current = false; setCancelBusy(false); }
  };

  const freezePaymentContext = async (
    baseWorkflow: OrderWorkflow,
    receiptCustomerId: string | null
  ) => {
    const taxInvoiceError = taxInvoiceValidationMessage();
    if (taxInvoiceError) throw new Error(taxInvoiceError);

    return updateCashierOrderContext(baseWorkflow, {
      serviceType: baseWorkflow.order.serviceType,
      customerId: receiptCustomerId,
      tableId: baseWorkflow.fulfillment.tableId,
      guestCount: baseWorkflow.fulfillment.guestCount,
      contactName: baseWorkflow.fulfillment.contactName,
      contactPhone: baseWorkflow.fulfillment.contactPhone,
      vehicleNumber: baseWorkflow.fulfillment.vehicleNumber,
      deliveryAddressId: baseWorkflow.fulfillment.deliveryAddressId,
      deliveryFeeMinor: baseWorkflow.order.deliveryFeeMinor,
      deliveryInstructions: baseWorkflow.fulfillment.deliveryInstructions,
      externalReference: baseWorkflow.order.externalReference,
      promisedAt: baseWorkflow.fulfillment.promisedAt,
      taxInvoiceRequested: contextTaxInvoiceRequested,
      taxInvoiceCompanyName: contextTaxInvoiceRequested
        ? contextTaxInvoiceCompanyName.trim()
        : null,
      taxInvoiceTaxRegistrationNumber: contextTaxInvoiceRequested
        ? normalizedTaxInvoiceTrn()
        : null,
      taxInvoiceCompanyAddress: contextTaxInvoiceRequested
        ? contextTaxInvoiceAddress.trim()
        : null
    });
  };

  const executePaymentCapture = async (
    base: OrderWorkflow,
    request: PaymentCaptureRequest
  ) => {
    const amountMinor = majorInputToMinor(request.amountMajor, base.order.currency);
    const tenderedMinor = majorInputToMinor(request.tenderedMajor, base.order.currency);
    if (amountMinor === null || tenderedMinor === null || amountMinor <= 0) {
      throw new Error(ar ? "مبلغ الدفع غير صالح." : "Invalid payment amount.");
    }
    try { return await captureCashierPayment(
      base,
      request.method,
      amountMinor,
      request.method === "Cash" ? tenderedMinor : amountMinor,
      request.method === "Card" ? "IntegratedTerminal" : null,
      request.providerReference,
      request.clientOperationId,
      request.method === "Wallet" || request.method === "Account" ? request.customerId : null
    );
    } catch (caught) {
      if ((caught as { problem?: { title?: string } })?.problem?.title === "ORDER_VERSION_CONFLICT") {
        const updated = await getOrderWorkflow(base.order.id);
        if (liveWorkflow.current?.order.id === base.order.id) {
          setWorkflow(updated); setPaymentOpen(false);
          setCouponNotice(base.appliedCoupon && !updated.appliedCoupon ? couponRemovedText : valueError(caught, language));
        }
      }
      throw caught;
    }
  };

  const captureSinglePayment = async (request: PaymentCaptureRequest) => {
    if (!workflow) return;
    const receiptCustomerId =
      request.customerId ?? workflow.customer?.id ?? (contextCustomerId || null);
    let paymentWorkflow = workflow;

    if (workflow.payments.length === 0) {
      paymentWorkflow = await freezePaymentContext(workflow, receiptCustomerId);
      setWorkflow(paymentWorkflow);
      hydrateContext(paymentWorkflow);
    }

    const result = await executePaymentCapture(paymentWorkflow, request);
    const paymentCompleted = result.orderCompleted || result.workflow.order.balanceDueMinor === 0;
    if (paymentCompleted) {
      clearActiveWorkspace();
    } else {
      setWorkflow(result.workflow);
      hydrateContext(result.workflow);
    }
    setPaymentOpen(false);
    if (result.issuedReceipt) {
      setReceiptWorkflow(result.workflow);
      setReceipt(result.issuedReceipt);
    }
    await refreshReferenceData();
  };

  const captureSplitPayment = async (
    request: SplitPaymentCaptureRequest
  ): Promise<SplitPaymentCaptureOutcome> => {
    if (!workflow) throw new Error(ar ? "لا يوجد طلب نشط." : "No active order.");

    let currentWorkflow = workflow;
    const receiptCustomerId =
      request.allocations.find((allocation) => allocation.customerId)?.customerId
      ?? currentWorkflow.customer?.id
      ?? (contextCustomerId || null);

    if (currentWorkflow.payments.length === 0) {
      currentWorkflow = await freezePaymentContext(
        currentWorkflow,
        receiptCustomerId
      );
      setWorkflow(currentWorkflow);
      hydrateContext(currentWorkflow);
    }

    let issuedReceipt: Receipt | null = null;
    const capturedOperationIds: string[] = [];

    for (let index = 0; index < request.allocations.length; index += 1) {
      const allocation = request.allocations[index];
      try {
        const result = await executePaymentCapture(currentWorkflow, allocation);
        currentWorkflow = result.workflow;
        if (result.issuedReceipt) issuedReceipt = result.issuedReceipt;
        capturedOperationIds.push(allocation.clientOperationId);
      } catch (caught) {
        if ((caught as { problem?: { title?: string } })?.problem?.title !== "ORDER_VERSION_CONFLICT") setWorkflow(currentWorkflow);
        return {
          completed: false,
          capturedOperationIds,
          failedOperationId: allocation.clientOperationId,
          remainingMinor: currentWorkflow.order.balanceDueMinor,
          errorMessage: caught instanceof Error ? caught.message : "Payment allocation failed."
        };
      }
    }

    if (currentWorkflow.order.balanceDueMinor === 0) {
      clearActiveWorkspace();
    } else {
      setWorkflow(currentWorkflow);
      hydrateContext(currentWorkflow);
    }
    setPaymentOpen(false);
    if (issuedReceipt) {
      setReceiptWorkflow(currentWorkflow);
      setReceipt(issuedReceipt);
    }
    await refreshReferenceData();
    return {
      completed: true,
      capturedOperationIds,
      failedOperationId: null,
      remainingMinor: currentWorkflow.order.balanceDueMinor,
      errorMessage: null
    };
  };

  const fiscalDocumentTitle = (document: Receipt) => {
    if (document.receiptType === "Refund" || document.fiscalDocumentType === "RefundReceipt") {
      return ar ? "إيصال استرداد" : "Refund Receipt";
    }
    if (document.fiscalDocumentType === "StandardTaxInvoice") {
      return ar ? "فاتورة ضريبية" : "Tax Invoice";
    }
    if (document.fiscalDocumentType === "SimplifiedTaxInvoice") {
      return ar ? "فاتورة ضريبية مبسطة" : "Simplified Tax Invoice";
    }
    return ar ? "مستند بيع" : "Sales Document";
  };

  const receiptDocumentTitle = (document: Receipt) => {
    if (document.receiptType === "Refund" || document.fiscalDocumentType === "RefundReceipt") {
      return ar ? "إيصال استرداد" : "Refund Receipt";
    }
    const containsCreditSale = document.payments.some(
      (payment) => payment.method === "Account"
        && payment.purpose !== "ReceivableSettlement"
    );
    if (containsCreditSale) {
      return ar ? "فاتورة بيع آجلة" : "Credit Sale Invoice";
    }
    const isCashOnly = document.payments.length > 0
      && document.payments.every((payment) => payment.method === "Cash");
    return isCashOnly
      ? ar ? "إيصال نقدي" : "Cash Receipt"
      : ar ? "إيصال دفع" : "Payment Receipt";
  };

  const receiptPaymentLabel = (payment: Receipt["payments"][number]) => {
    if (payment.purpose === "ReceivableSettlement") {
      return ar ? "تسوية رصيد عميل" : "Customer balance settlement";
    }
    if (payment.method === "Account") {
      return ar ? "حساب آجل / ذمم مدينة" : "On account / Accounts receivable";
    }
    return paymentMethodLabel(payment.method);
  };

  const receiptAccountOutstandingMinor = (document: Receipt) =>
    document.payments
      .filter(
        (payment) =>
          payment.method === "Account"
          && payment.purpose !== "ReceivableSettlement"
      )
      .reduce((total, payment) => total + payment.amountMinor, 0);

  const buildDocumentLineRows = (
    lines: Receipt["lines"],
    currency: string,
    deliveryFee?: {
      netMinor: number;
      taxMinor: number;
      totalMinor: number;
      taxRateBasisPoints: number;
    }
  ) => {
    const itemRows = lines
      .map(
        (line) => `
          <tr>
            <td class="item-cell">
              <strong>${escapeHtml(ar ? line.productNameAr : line.productNameEn)}</strong>
              <small>${escapeHtml(ar ? line.variantNameAr : line.variantNameEn)}</small>
            </td>
            <td>${line.quantityMilli / 1000}</td>
            <td>${escapeHtml(formatMoney(line.unitPriceBeforeTaxMinor ?? line.unitPriceMinor, currency))}</td>
            <td>${escapeHtml(formatMoney(line.discountBeforeTaxMinor ?? line.discountMinor, currency))}</td>
            <td>${escapeHtml(formatMoney(line.netMinor, currency))}</td>
            <td>${(line.taxRateBasisPoints / 100).toFixed(2)}%</td>
            <td>${escapeHtml(formatMoney(line.taxMinor, currency))}</td>
            <td>${escapeHtml(formatMoney(line.totalMinor, currency))}</td>
          </tr>`
      )
      .join("");

    const deliveryRow = deliveryFee && deliveryFee.totalMinor > 0
      ? `
          <tr>
            <td class="item-cell">
              <strong>${ar ? "رسوم التوصيل" : "Delivery fee"}</strong>
              <small>${ar ? "خدمة توصيل" : "Delivery service"}</small>
            </td>
            <td>1</td>
            <td>${escapeHtml(formatMoney(deliveryFee.netMinor, currency))}</td>
            <td>${escapeHtml(formatMoney(0, currency))}</td>
            <td>${escapeHtml(formatMoney(deliveryFee.netMinor, currency))}</td>
            <td>${(deliveryFee.taxRateBasisPoints / 100).toFixed(2)}%</td>
            <td>${escapeHtml(formatMoney(deliveryFee.taxMinor, currency))}</td>
            <td>${escapeHtml(formatMoney(deliveryFee.totalMinor, currency))}</td>
          </tr>`
      : "";

    return `${itemRows}${deliveryRow}`;
  };

  const printableDocumentStyles = `
    @page{margin:10mm}
    *{box-sizing:border-box}
    body{font-family:Arial,"Noto Sans Arabic",sans-serif;margin:0;color:#111;font-size:12px}
    h1,h2,p{margin:0}
    .document{max-width:820px;margin:0 auto}
    .document-header{text-align:center;border-bottom:2px solid #111;padding-bottom:12px}
    .document-title{margin-top:10px;font-size:22px;letter-spacing:.04em;text-transform:uppercase}
    .document-number{margin-top:5px;font-weight:700}
    .meta-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin:14px 0}
    .meta-box{padding:10px;border:1px solid #bbb;border-radius:6px;line-height:1.55}
    .meta-box strong{display:block;margin-bottom:4px}
    .meta-row{display:flex;justify-content:space-between;gap:16px;margin-top:4px}
    .identity-list{display:grid;gap:7px}
    .identity-row{display:grid;grid-template-columns:minmax(118px,auto) minmax(0,1fr);gap:12px;align-items:start}
    .identity-row span{font-weight:700;color:#444}
    .identity-row b{font-weight:600;overflow-wrap:anywhere}
    table{width:100%;border-collapse:collapse;margin:16px 0}
    th,td{padding:7px 5px;border:1px solid #bbb;text-align:start;vertical-align:top}
    th{background:#f2f2f2;font-size:10px}
    td:not(.item-cell),th:not(:first-child){white-space:nowrap;text-align:end}
    .item-cell small{display:block;color:#555;margin-top:3px}
    .totals{width:min(390px,100%);margin-inline-start:auto;border-top:2px solid #111}
    .total-row{display:flex;justify-content:space-between;gap:18px;padding:5px 0;border-bottom:1px solid #ddd}
    .total-row.grand{font-size:17px;font-weight:900;border-bottom:3px double #111}
    .payments{margin-top:14px;padding-top:10px;border-top:1px dashed #777}
    .footer{margin-top:18px;text-align:center;line-height:1.5}
    .print-button{margin-top:18px;padding:10px 18px;border:0;border-radius:5px;background:#111;color:#fff;cursor:pointer}
    @media(max-width:620px){.meta-grid{grid-template-columns:1fr}.identity-row{grid-template-columns:1fr;gap:2px}body{font-size:11px}th,td{padding:5px 3px}}
    @media print{.print-button{display:none}.document{max-width:none}}
  `;

  const printReceipt = (
    document: Receipt,
    mode: "payment" | "fiscal" = "payment"
  ) => {
    const receiptWindow = window.open("", "_blank", "width=900,height=820");
    if (!receiptWindow) return;
    const fiscalTitle = fiscalDocumentTitle(document);
    const title = mode === "fiscal" ? fiscalTitle : receiptDocumentTitle(document);
    const lineRows = buildDocumentLineRows(document.lines, document.currency, {
      netMinor: document.deliveryFeeNetMinor,
      taxMinor: document.deliveryFeeTaxMinor,
      totalMinor: document.deliveryFeeTotalMinor,
      taxRateBasisPoints: document.deliveryFeeTaxRateBasisPoints
    });
    const paymentRows = document.payments
      .map(
        (payment) => `
          <div class="meta-row" data-payment-method="${payment.method}" data-amount-minor="${payment.amountMinor}">
            <span>${escapeHtml(receiptPaymentLabel(payment))}</span>
            <strong>${escapeHtml(formatMoney(payment.amountMinor, document.currency))}</strong>
          </div>`
      )
      .join("");
    const accountOutstandingMinor = receiptAccountOutstandingMinor(document);
    const discounts = receiptDiscountSummary(document, ar);
    const discountRows = discounts.rows.map(row =>
      `<div class="total-row" data-discount="${row.key}" data-amount-minor="${row.amountMinor}"><span>${escapeHtml(row.label)}</span><strong dir="ltr">${escapeHtml(formatMoney(-row.amountMinor, document.currency))}</strong></div>`
    ).join("");
    const accountOutstandingRow = accountOutstandingMinor > 0
      ? `<div class="meta-row"><span>${ar ? "الرصيد المستحق على العميل" : "Customer outstanding balance"}</span><strong>${escapeHtml(formatMoney(accountOutstandingMinor, document.currency))}</strong></div>`
      : "";
    const recipientBlock = document.customerCompanyName
      ? `<div class="meta-box">
          <strong>${ar ? "بيانات العميل / الشركة" : "Customer / company details"}</strong>
          <div class="identity-list">
            <div class="identity-row"><span>${ar ? "اسم العميل / الشركة" : "Customer / company name"}</span><b>${escapeHtml(document.customerCompanyName)}</b></div>
            <div class="identity-row"><span>${ar ? "الموقع / العنوان" : "Location / address"}</span><b>${escapeHtml(document.customerCompanyAddress ?? "—")}</b></div>
            ${document.customerPhone ? `<div class="identity-row"><span>${ar ? "رقم الهاتف" : "Phone"}</span><b dir="ltr">${escapeHtml(document.customerPhone)}</b></div>` : ""}
            <div class="identity-row"><span>${ar ? "الرقم الضريبي" : "TRN"}</span><b dir="ltr">${escapeHtml(document.customerTaxRegistrationNumber ?? "—")}</b></div>
          </div>
        </div>`
      : `<div class="meta-box">
          <strong>${ar ? "بيانات العميل" : "Customer details"}</strong>
          <div class="identity-list">
            <div class="identity-row"><span>${ar ? "اسم العميل" : "Customer name"}</span><b>${escapeHtml(document.customerName ?? "—")}</b></div>
            <div class="identity-row"><span>${ar ? "رقم الهاتف" : "Phone"}</span><b dir="ltr">${escapeHtml(document.customerPhone ?? "—")}</b></div>
          </div>
        </div>`;

    receiptWindow.document.write(`
      <!doctype html>
      <html lang="${ar ? "ar" : "en"}" dir="${ar ? "rtl" : "ltr"}">
      <head>
        <meta charset="utf-8">
        <title>${escapeHtml(title)} — ${escapeHtml(document.receiptNumber)}</title>
        <style>${printableDocumentStyles}</style>
      </head>
      <body>
        <main class="document">
          <header class="document-header">
            <h1>${escapeHtml(document.businessName || context.tenantName)}</h1>
            <p>${escapeHtml(document.branchName)}</p>
            <p>${escapeHtml(document.branchAddress ?? "")}</p>
            <p>${ar ? "الرقم الضريبي للمورد" : "Supplier TRN"}: ${escapeHtml(document.taxRegistrationNumber ?? "—")}</p>
            <h2 class="document-title">${escapeHtml(title)}</h2>
            <p class="document-number">${escapeHtml(document.receiptNumber)}</p>
            ${document.revisionNumber > 1
              ? `<p>${escapeHtml(ar ? `إعادة إصدار رقم ${document.revisionNumber}` : `Reissued revision ${document.revisionNumber}`)}</p>`
              : ""}
          </header>
          <section class="meta-grid">
            <div class="meta-box">
              <strong>${mode === "fiscal"
                ? ar ? "بيانات الفاتورة" : "Invoice details"
                : ar ? "بيانات إيصال الدفع" : "Payment receipt details"}</strong>
              <div class="meta-row"><span>${ar ? "رقم الطلب" : "Order"}</span><b>${escapeHtml(displayOrderNumber(document.orderNumber))}</b></div>
              <div class="meta-row"><span>${ar ? "تاريخ الإصدار" : "Issue date"}</span><b>${escapeHtml(new Date(document.issuedAt).toLocaleString(ar ? "ar-AE" : "en-AE"))}</b></div>
              <div class="meta-row"><span>${ar ? "تاريخ التوريد" : "Supply date"}</span><b>${escapeHtml(new Date(document.supplyDate).toLocaleString(ar ? "ar-AE" : "en-AE"))}</b></div>
              <div class="meta-row"><span>${ar ? "نوع الخدمة" : "Service"}</span><b>${escapeHtml(document.serviceType)}</b></div>
              ${document.tableName ? `<div class="meta-row"><span>${ar ? "الطاولة" : "Table"}</span><b>${escapeHtml(document.tableName)}</b></div>` : ""}
            </div>
            ${recipientBlock}
          </section>
          <table>
            <thead><tr>
              <th>${ar ? "الصنف" : "Item"}</th>
              <th>${ar ? "الكمية" : "Qty"}</th>
              <th>${document.lines.every(line => line.unitPriceBeforeTaxMinor != null) ? (ar ? "سعر الوحدة قبل الضريبة" : "Unit price before tax") : (ar ? "سعر الوحدة" : "Unit price")}</th>
              <th>${document.lines.every(line => line.discountBeforeTaxMinor != null) ? (ar ? "الخصم قبل الضريبة" : "Discount before tax") : (ar ? "الخصم" : "Discount")}</th>
              <th>${ar ? "الصافي" : "Net"}</th>
              <th>${ar ? "نسبة الضريبة" : "VAT rate"}</th>
              <th>${ar ? "الضريبة" : "VAT"}</th>
              <th>${ar ? "الإجمالي" : "Total"}</th>
            </tr></thead>
            <tbody>${lineRows}</tbody>
          </table>
          <section class="totals">
            ${orderFinancialRows(document.financialSummary, document, ar).map(row => `<div class="total-row ${row.key === "total" ? "grand" : ""}" data-financial="${row.key}" data-amount-minor="${row.amountMinor}"><span>${escapeHtml(row.label)}</span><strong>${escapeHtml(formatMoney(row.amountMinor, document.currency))}</strong></div>`).join("")}
            ${discountRows ? `<div class="discount-details"><p>${ar ? "تفاصيل الخصم حسب أساس التسعير" : "Discount details on the pricing basis"}</p>${discountRows}</div>` : ""}
          </section>
          <section class="payments">
            <strong>${ar ? "طرق الدفع" : "Payments"}</strong>
            ${paymentRows || `<p>${ar ? "لا توجد دفعات" : "No payments"}</p>`}
            ${document.payments.some(p => p.method === "GiftCard") ? `<div class="meta-row" data-paid-minor="${document.payments.reduce((sum, p) => sum + p.amountMinor, 0)}"><span>${ar ? "إجمالي المدفوع" : "Total paid"}</span><strong>${escapeHtml(formatMoney(document.payments.reduce((sum, p) => sum + p.amountMinor, 0), document.currency))}</strong></div>` : ""}
            ${accountOutstandingRow}
          </section>
          <footer class="footer">
            <p>${escapeHtml(ar ? document.footerMessageAr ?? "" : document.footerMessageEn ?? "")}</p>
            <button class="print-button" onclick="window.print()">${ar ? "طباعة" : "Print"}</button>
          </footer>
        </main>
      </body></html>
    `);
    receiptWindow.document.close();
  };

  const printPreBill = (document: PreBill, targetWindow?: Window | null) => {
    const preBillWindow = targetWindow ?? window.open("", "_blank", "width=900,height=820");
    if (!preBillWindow) return;
    const discountRows = receiptDiscountSummary(document, ar).rows.map(row =>
      `<div class="total-row" data-discount="${row.key}" data-amount-minor="${row.amountMinor}"><span>${escapeHtml(row.label)}</span><strong dir="ltr">${escapeHtml(formatMoney(-row.amountMinor, document.currency))}</strong></div>`
    ).join("");
    const lineRows = buildDocumentLineRows(document.lines, document.currency, {
      netMinor: document.deliveryFeeNetMinor,
      taxMinor: document.deliveryFeeTaxMinor,
      totalMinor: document.deliveryFeeTotalMinor,
      taxRateBasisPoints: document.deliveryFeeTaxRateBasisPoints
    });
    const documentTitle = ar ? document.documentTitleAr : document.documentTitleEn;
    const historicalPayments = (document.payments ?? []).some(p => p.method === "GiftCard") ? document.payments ?? [] : [];
    const paidMinor = historicalPayments.reduce((sum, p) => sum + p.amountMinor, 0);
    const giftPaymentRows = historicalPayments.map(p => `<div class="meta-row" data-payment-method="${p.method}" data-amount-minor="${p.amountMinor}"><span>${escapeHtml(receiptPaymentLabel(p))}</span><strong>${escapeHtml(formatMoney(p.amountMinor, document.currency))}</strong></div>`).join("");
    const recipientBlock = document.customerCompanyName
      ? `<div class="meta-box">
          <strong>${ar ? "بيانات العميل / الشركة" : "Customer / company details"}</strong>
          <div class="identity-list">
            <div class="identity-row"><span>${ar ? "اسم العميل / الشركة" : "Customer / company name"}</span><b>${escapeHtml(document.customerCompanyName)}</b></div>
            <div class="identity-row"><span>${ar ? "الموقع / العنوان" : "Location / address"}</span><b>${escapeHtml(document.customerCompanyAddress ?? "—")}</b></div>
            ${document.customerPhone ? `<div class="identity-row"><span>${ar ? "رقم الهاتف" : "Phone"}</span><b dir="ltr">${escapeHtml(document.customerPhone)}</b></div>` : ""}
            <div class="identity-row"><span>${ar ? "الرقم الضريبي" : "TRN"}</span><b dir="ltr">${escapeHtml(document.customerTaxRegistrationNumber ?? "—")}</b></div>
          </div>
        </div>`
      : `<div class="meta-box">
          <strong>${ar ? "بيانات العميل" : "Customer details"}</strong>
          <div class="identity-list">
            <div class="identity-row"><span>${ar ? "اسم العميل" : "Customer name"}</span><b>${escapeHtml(document.customerName ?? "—")}</b></div>
            <div class="identity-row"><span>${ar ? "رقم الهاتف" : "Phone"}</span><b dir="ltr">${escapeHtml(document.customerPhone ?? "—")}</b></div>
          </div>
        </div>`;

    preBillWindow.document.open();
    preBillWindow.document.write(`
      <!doctype html>
      <html lang="${ar ? "ar" : "en"}" dir="${ar ? "rtl" : "ltr"}">
      <head><meta charset="utf-8"><title>${escapeHtml(documentTitle)} — ${escapeHtml(document.orderNumber)}</title><style>${printableDocumentStyles}</style></head>
      <body><main class="document">
        <header class="document-header">
          <h1>${escapeHtml(document.businessName || context.tenantName)}</h1>
          <p>${escapeHtml(document.branchName)}</p>
          <p>${escapeHtml(document.branchAddress ?? "")}</p>
          <p>${ar ? "الرقم الضريبي للمورد" : "Supplier TRN"}: ${escapeHtml(document.taxRegistrationNumber ?? "—")}</p>
          <h2 class="document-title">${escapeHtml(documentTitle)}</h2>
          <p class="document-number">${escapeHtml(displayOrderNumber(document.preBillNumber))} · ${ar ? "غير مسددة" : "UNPAID"}</p>
        </header>
        <section class="meta-grid">
          <div class="meta-box">
            <strong>${ar ? "بيانات الطلب" : "Order details"}</strong>
            <div class="meta-row"><span>${ar ? "رقم الطلب" : "Order"}</span><b>${escapeHtml(displayOrderNumber(document.orderNumber))}</b></div>
            <div class="meta-row"><span>${ar ? "وقت الإصدار" : "Generated"}</span><b>${escapeHtml(new Date(document.generatedAt).toLocaleString(ar ? "ar-AE" : "en-AE"))}</b></div>
            <div class="meta-row"><span>${ar ? "نوع الخدمة" : "Service"}</span><b>${escapeHtml(document.serviceType)}</b></div>
            ${document.tableName ? `<div class="meta-row"><span>${ar ? "الطاولة" : "Table"}</span><b>${escapeHtml(document.tableName)}</b></div>` : ""}
          </div>
          ${recipientBlock}
        </section>
        <table><thead><tr>
          <th>${ar ? "الصنف" : "Item"}</th><th>${ar ? "الكمية" : "Qty"}</th><th>${document.lines.every(line => line.unitPriceBeforeTaxMinor != null) ? (ar ? "سعر الوحدة قبل الضريبة" : "Unit price before tax") : (ar ? "سعر الوحدة" : "Unit price")}</th><th>${document.lines.every(line => line.discountBeforeTaxMinor != null) ? (ar ? "الخصم قبل الضريبة" : "Discount before tax") : (ar ? "الخصم" : "Discount")}</th><th>${ar ? "الصافي" : "Net"}</th><th>${ar ? "نسبة الضريبة" : "VAT rate"}</th><th>${ar ? "الضريبة" : "VAT"}</th><th>${ar ? "الإجمالي" : "Total"}</th>
        </tr></thead><tbody>${lineRows}</tbody></table>
        <section class="totals">
            ${orderFinancialRows(document.financialSummary, document, ar).map(row => `<div class="total-row ${row.key === "total" ? "grand" : ""}" data-financial="${row.key}" data-amount-minor="${row.amountMinor}"><span>${escapeHtml(row.label)}</span><strong>${escapeHtml(formatMoney(row.amountMinor, document.currency))}</strong></div>`).join("")}
            ${discountRows ? `<div class="discount-details"><p>${ar ? "تفاصيل الخصم حسب أساس التسعير" : "Discount details on the pricing basis"}</p>${discountRows}</div>` : ""}
          </section>
        ${historicalPayments.length ? `<section class="payments">${giftPaymentRows}<div class="meta-row" data-paid-minor="${paidMinor}"><span>${ar ? "إجمالي المدفوع" : "Total paid"}</span><strong>${escapeHtml(formatMoney(paidMinor, document.currency))}</strong></div><div class="meta-row"><span>${ar ? "المتبقي" : "Balance due"}</span><strong>${escapeHtml(formatMoney(Math.max(0, document.totalMinor - paidMinor), document.currency))}</strong></div></section>` : ""}
        <footer class="footer"><button class="print-button" onclick="window.print()">${ar ? "طباعة" : "Print"}</button></footer>
      </main></body></html>
    `);
    preBillWindow.document.close();
  };

  const printShiftClosureReport = (summary: ShiftSummary) => {
    const reportWindow = window.open("", "_blank", "width=900,height=900");
    if (!reportWindow) return;

    const paymentRows = summary.paymentMethods
      .filter(method => method.method !== "GiftCard" || method.totalCollectionsMinor !== 0 || method.refundsMinor !== 0 || (method.giftCardForfeitMinor ?? 0) > 0)
      .map(
        (method) => `<tr>
          <td>${escapeHtml(paymentMethodLabel(method.method))}${(method.giftCardFundingMinor ?? 0) > 0 ? `<small>${ar ? "تحصيل إصدار بطاقات الهدايا" : "Gift card issuance collections"}: ${escapeHtml(formatMoney(method.giftCardFundingMinor!, summary.currency))}</small>` : ""}${(method.giftCardForfeitMinor ?? 0) > 0 ? `<small>${ar ? "عربون محتفظ به" : "Retained deposit"}: ${escapeHtml(formatMoney(method.giftCardForfeitMinor!, summary.currency))}</small>` : ""}</td>
          <td>${method.paymentCount}</td>
          <td>${method.creditSaleCount}</td>
          <td>${escapeHtml(formatMoney(method.saleSettlementMinor, summary.currency))}</td>
          <td>${escapeHtml(formatMoney(method.creditSalesMinor, summary.currency))}</td>
          <td>${escapeHtml(formatMoney(method.depositMinor, summary.currency))}</td>
          <td>${escapeHtml(formatMoney(method.balanceSettlementMinor, summary.currency))}</td>
          <td>${escapeHtml(formatMoney(method.receivableSettlementMinor, summary.currency))}</td>
          <td>${escapeHtml(formatMoney(method.totalCollectionsMinor, summary.currency))}</td>
          <td>${escapeHtml(formatMoney(method.depositRefundsMinor, summary.currency))}</td>
          <td>${escapeHtml(formatMoney(method.balanceRefundsMinor, summary.currency))}</td>
          <td>${escapeHtml(formatMoney(method.refundsMinor, summary.currency))}</td>
          <td><strong>${escapeHtml(formatMoney(method.netMinor, summary.currency))}</strong></td>
        </tr>`
      )
      .join("");
    const serviceLabels: Record<ServiceType, string> = {
      WalkIn: ar ? "استلام" : "Pickup",
      DineIn: ar ? "داخلي" : "Dine-in",
      Takeaway: ar ? "سفري" : "Takeaway",
      Delivery: ar ? "توصيل" : "Delivery"
    };
    const serviceRows = summary.serviceTypes
      .filter((service) => service.orderCount > 0 || service.grossMinor !== 0)
      .map((service) => `<tr>
          <td>${escapeHtml(serviceLabels[service.serviceType])}</td>
          <td>${service.orderCount}</td>
          <td>${escapeHtml(formatMoney(service.subtotalMinor, summary.currency))}</td>
          <td>${escapeHtml(formatMoney(service.discountMinor, summary.currency))}</td>
          <td>${escapeHtml(formatMoney(service.netMinor, summary.currency))}</td>
          <td>${escapeHtml(formatMoney(service.taxMinor, summary.currency))}</td>
          <td><strong>${escapeHtml(formatMoney(service.grossMinor, summary.currency))}</strong></td>
        </tr>`)
      .join("");
    const movementRows = summary.movements.length
      ? summary.movements.map((movement) => `<tr>
          <td>${escapeHtml(new Date(movement.createdAt).toLocaleString(ar ? "ar-AE" : "en-AE"))}</td>
          <td>${movement.movementType === "CashIn"
            ? ar ? "إدخال نقدي" : "Cash in"
            : ar ? "إخراج نقدي" : "Cash out"}</td>
          <td>${escapeHtml(movement.reason)}</td>
          <td>${escapeHtml(formatMoney(movement.amountMinor, summary.currency))}</td>
        </tr>`).join("")
      : `<tr><td colspan="4">${ar ? "لا توجد حركات نقدية" : "No cash movements"}</td></tr>`;

    reportWindow.document.write(`
      <!doctype html>
      <html lang="${ar ? "ar" : "en"}" dir="${ar ? "rtl" : "ltr"}">
      <head>
        <meta charset="utf-8">
        <title>${ar ? "تقرير إغلاق الوردية" : "Shift Closure Z-Report"} — ${escapeHtml(summary.reportNumber)}</title>
        <style>
          ${printableDocumentStyles}
          .z-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px;margin:14px 0}
          .z-section{margin-top:18px}
          .z-section h3{margin:0 0 8px;padding-bottom:5px;border-bottom:2px solid #111}
          .signature-grid{display:grid;grid-template-columns:1fr 1fr;gap:45px;margin-top:45px}
          .signature{padding-top:30px;border-top:1px solid #111;text-align:center}
          .variance{font-size:17px;font-weight:900}
          @media(max-width:620px){.z-grid,.signature-grid{grid-template-columns:1fr}}
        </style>
      </head>
      <body>
        <main class="document">
          <header class="document-header">
            <h1>${escapeHtml(summary.businessName || context.tenantName)}</h1>
            <p>${escapeHtml(summary.branchName)}</p>
            <h2 class="document-title">${ar ? "تقرير إغلاق الوردية (Z)" : "SHIFT CLOSURE Z-REPORT"}</h2>
            <p class="document-number">${escapeHtml(summary.reportNumber)}</p>
          </header>

          <section class="meta-grid">
            <div class="meta-box">
              <strong>${ar ? "بيانات الوردية" : "Shift details"}</strong>
              <div class="meta-row"><span>${ar ? "الكاشير" : "Cashier"}</span><b>${escapeHtml(summary.cashierName)}</b></div>
              <div class="meta-row"><span>${ar ? "وقت الفتح" : "Opened"}</span><b>${escapeHtml(new Date(summary.openedAt).toLocaleString(ar ? "ar-AE" : "en-AE"))}</b></div>
              <div class="meta-row"><span>${ar ? "وقت الإغلاق" : "Closed"}</span><b>${escapeHtml(summary.closedAt ? new Date(summary.closedAt).toLocaleString(ar ? "ar-AE" : "en-AE") : "—")}</b></div>
              <div class="meta-row"><span>${ar ? "عدد الطلبات" : "Orders"}</span><b>${summary.orderCount}</b></div>
              <div class="meta-row"><span>${ar ? "عدد الدفعات" : "Payments"}</span><b>${summary.paymentCount}</b></div>
            </div>
            <div class="meta-box">
              <strong>${ar ? "مطابقة الصندوق" : "Cash drawer reconciliation"}</strong>
              <div class="meta-row"><span>${ar ? "الرصيد الافتتاحي" : "Opening float"}</span><b>${escapeHtml(formatMoney(summary.openingCashMinor, summary.currency))}</b></div>
              <div class="meta-row"><span>${ar ? "النقد المتوقع" : "Expected cash"}</span><b>${escapeHtml(formatMoney(summary.expectedCashMinor, summary.currency))}</b></div>
              <div class="meta-row"><span>${ar ? "النقد المعدود" : "Counted cash"}</span><b>${escapeHtml(formatMoney(summary.countedCashMinor ?? 0, summary.currency))}</b></div>
              <div class="meta-row variance"><span>${ar ? "فرق الصندوق" : "Variance"}</span><b>${escapeHtml(formatMoney(summary.varianceMinor ?? 0, summary.currency))}</b></div>
            </div>
          </section>

          <section class="z-section">
            <h3>${ar ? "الملخص الضريبي والإيرادات" : "Tax and revenue summary"}</h3>
            <div class="z-grid">
              <div class="meta-box"><span>${ar ? "المبيعات الصافية دون ضريبة" : "Net sales before VAT"}</span><strong>${escapeHtml(formatMoney(summary.netSalesMinor, summary.currency))}</strong></div>
              <div class="meta-box"><span>${ar ? "الضريبة المحصلة" : "VAT collected"}</span><strong>${escapeHtml(formatMoney(summary.taxCollectedMinor, summary.currency))}</strong></div>
              <div class="meta-box"><span>${ar ? "إجمالي المبيعات" : "Gross sales"}</span><strong>${escapeHtml(formatMoney(summary.grossSalesMinor, summary.currency))}</strong></div>
              <div class="meta-box"><span>${ar ? "إجمالي المرتجعات" : "Total refunds"}</span><strong>${escapeHtml(formatMoney(summary.totalRefundsMinor, summary.currency))}</strong></div>
              <div class="meta-box"><span>${ar ? "صافي الضريبة بعد المرتجعات" : "Net VAT after refunds"}</span><strong>${escapeHtml(formatMoney(summary.netTaxAfterRefundsMinor, summary.currency))}</strong></div>
              <div class="meta-box"><span>${ar ? "صافي الإيرادات" : "Net revenue"}</span><strong>${escapeHtml(formatMoney(summary.netRevenueMinor, summary.currency))}</strong></div>
            </div>
          </section>

          <section class="z-section">
            <h3>${ar ? "المقبوضات حسب الغرض" : "Collections by purpose"}</h3>
            <div class="z-grid">
              <div class="meta-box"><span>${ar ? "مبيعات آجلة جديدة" : "New credit sales"}</span><strong>${escapeHtml(formatMoney(summary.creditSalesMinor, summary.currency))}</strong></div>
              <div class="meta-box"><span>${ar ? "عربون الطلبات المستقبلية" : "Future-order deposits"}</span><strong>${escapeHtml(formatMoney(summary.depositCollectedMinor, summary.currency))}</strong></div>
              <div class="meta-box"><span>${ar ? "تسوية أرصدة الطلبات المستقبلية" : "Future-order balance settlements"}</span><strong>${escapeHtml(formatMoney(summary.balanceSettlementCollectedMinor, summary.currency))}</strong></div>
              <div class="meta-box"><span>${ar ? "تحصيل الذمم المدينة" : "Customer account settlements"}</span><strong>${escapeHtml(formatMoney(summary.receivableSettlementCollectedMinor, summary.currency))}</strong></div>
              <div class="meta-box"><span>${ar ? "استرداد العربون" : "Deposit refunds"}</span><strong>${escapeHtml(formatMoney(summary.preOrderDepositRefundsMinor, summary.currency))}</strong></div>
              <div class="meta-box"><span>${ar ? "استرداد الرصيد" : "Balance refunds"}</span><strong>${escapeHtml(formatMoney(summary.preOrderBalanceRefundsMinor, summary.currency))}</strong></div>
              <div class="meta-box"><span>${ar ? "العربون المصادَر" : "Forfeited deposits"}</span><strong>${escapeHtml(formatMoney(summary.forfeitedDepositsMinor, summary.currency))}</strong></div>
              <div class="meta-box"><span>${ar ? "إجمالي المقبوضات قبل الاسترداد" : "Gross collections before refunds"}</span><strong>${escapeHtml(formatMoney(summary.totalCollectionsMinor, summary.currency))}</strong></div>
              <div class="meta-box"><span>${ar ? "صافي المقبوضات بعد جميع الاستردادات" : "Net collections after all refunds"}</span><strong>${escapeHtml(formatMoney(summary.totalCollectionsMinor - summary.totalRefundsMinor - summary.preOrderDepositRefundsMinor - summary.preOrderBalanceRefundsMinor, summary.currency))}</strong></div>
              <div class="meta-box"><span>${ar ? "صافي الإيرادات المالية" : "Fiscal net revenue"}</span><strong>${escapeHtml(formatMoney(summary.netRevenueMinor, summary.currency))}</strong></div>
            </div>
          </section>

          <section class="z-section">
            <h3>${ar ? "حركة النقد" : "Cash movement summary"}</h3>
            <div class="z-grid">
              <div class="meta-box"><span>${ar ? "المبيعات النقدية" : "Cash sales"}</span><strong>${escapeHtml(formatMoney(summary.cashSalesMinor, summary.currency))}</strong></div>
              <div class="meta-box"><span>${ar ? "المرتجعات النقدية" : "Cash refunds"}</span><strong>${escapeHtml(formatMoney(summary.cashRefundsMinor, summary.currency))}</strong></div>
              <div class="meta-box"><span>${ar ? "إدخال نقدي" : "Cash in"}</span><strong>${escapeHtml(formatMoney(summary.cashInMinor, summary.currency))}</strong></div>
              <div class="meta-box"><span>${ar ? "إخراج نقدي" : "Cash out"}</span><strong>${escapeHtml(formatMoney(summary.cashOutMinor, summary.currency))}</strong></div>
            </div>
          </section>

          <section class="z-section">
            <h3>${ar ? "المبيعات حسب نوع الطلب" : "Sales by service type"}</h3>
            <table><thead><tr>
              <th>${ar ? "نوع الطلب" : "Service type"}</th>
              <th>${ar ? "عدد الطلبات" : "Orders"}</th>
              <th>${ar ? "قبل الخصم" : "Subtotal"}</th>
              <th>${ar ? "الخصومات" : "Discounts"}</th>
              <th>${ar ? "الصافي" : "Net"}</th>
              <th>${ar ? "الضريبة" : "VAT"}</th>
              <th>${ar ? "الإجمالي" : "Gross"}</th>
            </tr></thead><tbody>${serviceRows || `<tr><td colspan="7">${ar ? "لا توجد مبيعات مكتملة" : "No completed sales"}</td></tr>`}</tbody></table>
            <div class="meta-box">
              <strong>${ar ? "الطلبات المستقبلية — مجموعة فرعية وليست إضافة على الإجمالي" : "Future orders — subset, not added again to totals"}</strong>
              <div class="meta-row"><span>${ar ? "عدد الطلبات المستقبلية المكتملة" : "Completed future orders"}</span><b>${summary.futureOrderCount}</b></div>
              <div class="meta-row"><span>${ar ? "الصافي" : "Net"}</span><b>${escapeHtml(formatMoney(summary.futureOrderNetMinor, summary.currency))}</b></div>
              <div class="meta-row"><span>${ar ? "الضريبة" : "VAT"}</span><b>${escapeHtml(formatMoney(summary.futureOrderTaxMinor, summary.currency))}</b></div>
              <div class="meta-row"><span>${ar ? "الإجمالي" : "Gross"}</span><b>${escapeHtml(formatMoney(summary.futureOrderGrossMinor, summary.currency))}</b></div>
            </div>
          </section>

          <section class="z-section">
            <h3>${ar ? "تفصيل طرق الدفع" : "Payment method breakdown"}</h3>
            <table><thead><tr>
              <th>${ar ? "الطريقة" : "Method"}</th>
              <th>${ar ? "عدد التحصيلات" : "Collection count"}</th>
              <th>${ar ? "عدد المبيعات الآجلة" : "Credit-sale count"}</th>
              <th>${ar ? "تسوية المبيعات" : "Sale settlements"}</th>
              <th>${ar ? "مبيعات آجلة" : "Credit sales"}</th>
              <th>${ar ? "العربون" : "Deposits"}</th>
              <th>${ar ? "أرصدة مستقبلية" : "Future balances"}</th>
              <th>${ar ? "تحصيل ذمم" : "A/R settlements"}</th>
              <th>${ar ? "إجمالي المقبوضات" : "Total collections"}</th>
              <th>${ar ? "استرداد العربون" : "Deposit refunds"}</th>
              <th>${ar ? "استرداد الرصيد" : "Balance refunds"}</th>
              <th>${ar ? "إجمالي المسترد" : "Total refunds"}</th>
              <th>${ar ? "الصافي" : "Net"}</th>
            </tr></thead><tbody>${paymentRows}</tbody></table>
          </section>

          <section class="z-section">
            <h3>${ar ? "الحركات النقدية التفصيلية" : "Detailed cash movements"}</h3>
            <table><thead><tr>
              <th>${ar ? "الوقت" : "Time"}</th>
              <th>${ar ? "النوع" : "Type"}</th>
              <th>${ar ? "السبب" : "Reason"}</th>
              <th>${ar ? "المبلغ" : "Amount"}</th>
            </tr></thead><tbody>${movementRows}</tbody></table>
          </section>

          ${summary.closingNote ? `<section class="meta-box"><strong>${ar ? "ملاحظة الإغلاق" : "Closing note"}</strong><p>${escapeHtml(summary.closingNote)}</p></section>` : ""}

          <section class="signature-grid">
            <div class="signature">${ar ? "توقيع الكاشير" : "Cashier signature"}</div>
            <div class="signature">${ar ? "توقيع المدير / المستلم" : "Manager / handover signature"}</div>
          </section>
          <footer class="footer">
            <button class="print-button" onclick="window.print()">${ar ? "طباعة التقرير" : "Print report"}</button>
          </footer>
        </main>
      </body></html>
    `);
    reportWindow.document.close();
  };

  const printPreBillForOrder = async (
    orderId: string,
    expectedOrderVersion: number
  ) => {
    if (!offline.online) {
      setError(
        ar
          ? "طباعة الفاتورة قبل الدفع تحتاج اتصالاً للتأكد من الأسعار والضريبة الحالية."
          : "Pre-payment invoice printing requires connectivity to confirm current prices and tax."
      );
      return;
    }

    // Open the print target synchronously from the user's click. Opening it only
    // after the API request resolves is commonly blocked by browser popup rules.
    const preBillWindow = window.open("", "_blank", "width=900,height=820");
    if (!preBillWindow) {
      setError(
        ar
          ? "اسمح بالنوافذ المنبثقة لهذا الموقع لطباعة الفاتورة قبل الدفع."
          : "Allow pop-ups for this site to print the pre-payment invoice."
      );
      return;
    }
    preBillWindow.document.open();
    preBillWindow.document.write(
      `<!doctype html><html lang="${ar ? "ar" : "en"}" dir="${ar ? "rtl" : "ltr"}"><head><meta charset="utf-8"><title>${
        ar ? "جارٍ تجهيز الفاتورة قبل الدفع" : "Preparing pre-payment invoice"
      }</title></head><body><p style="font-family:Arial,sans-serif;padding:24px">${
        ar ? "جارٍ تجهيز الفاتورة قبل الدفع…" : "Preparing pre-payment invoice…"
      }</p></body></html>`
    );
    preBillWindow.document.close();

    try {
      setPrintingPreBillOrderId(orderId);
      const document = await generateCashierPreBill(
        orderId,
        expectedOrderVersion
      );
      printPreBill(document, preBillWindow);
      toast.notify({
        tone: "success",
        title: ar ? "تم تجهيز الفاتورة قبل الدفع" : "Pre-payment invoice prepared",
        message: ar
          ? "المستند غير مسدد ومخصص للمراجعة؛ وهو ليس إيصال دفع أو إثبات سداد."
          : "The document is unpaid and for review; it is not a payment receipt or proof of payment."
      });
    } catch (caught) {
      preBillWindow.close();
      handleError(caught);
    } finally {
      setPrintingPreBillOrderId(null);
    }
  };



  const refundableLines = useMemo(() => {
    if (!workflow) return [];
    const refunded = new Map<string, number>();
    refunds.filter((refund) => refund.status === "Completed").forEach((refund) => {
      refund.lines.forEach((line) => refunded.set(line.originalOrderLineId, (refunded.get(line.originalOrderLineId) ?? 0) + line.quantityMilli));
    });
    const source = workflow.receipt?.lines.map((line) => ({
      id: line.orderLineId,
      name: ar ? line.productNameAr : line.productNameEn,
      quantityMilli: line.quantityMilli
    })) ?? workflow.cart.lines.map((line) => ({
      id: line.id,
      name: ar ? line.productNameAr : line.productNameEn,
      quantityMilli: line.quantityMilli
    }));
    return source.map((line) => ({
      ...line,
      availableQuantityMilli: Math.max(0, line.quantityMilli - (refunded.get(line.id) ?? 0))
    })).filter((line) => line.availableQuantityMilli > 0);
  }, [workflow, refunds, ar]);

  const refundLines = refundableLines.map((line) => ({
      originalOrderLineId: line.id,
      quantityMilli: Math.min(line.availableQuantityMilli, refundQuantities[line.id] ?? 0)
    })).filter((line) => line.quantityMilli > 0);
  const allocationPreview = useRefundPreview(workflow?.order.id, workflow?.order.version, refundLines, refundOpen && !refundPending);

  const processRefund = async () => {
    if (!workflow || refundBusyRef.current) return;
    const lines = refundLines;
    if (!refundPending && (!lines.length || !refundReason.trim() || !allocationPreview.preview)) return;
    refundBusyRef.current = true; setRefundBusy(true);
    try {
      const args = refundPending ?? [workflow, refundReason.trim(), lines, createIdempotencyKey(),
        giftReplacementRequired ? giftReplacementInput(giftNoExpiry, giftExpiry, language) : undefined, allocationPreview.preview!.previewToken] as Parameters<typeof requestCashierRefund>;
      setRefundPending(args);
      const result = await requestCashierRefund(...args);
      setRefundPending(null); setGiftReplacementRequired(false); setGiftNoExpiry(false); setGiftExpiry("");
      if (result.approvalRequired) {
        setRefundOpen(false);
        setRefundReason("");
        setRefundQuantities({});
        setApprovalNotice({
          operation: "Refund",
          requestId: result.approvalRequestId ?? undefined,
          expiresAt: result.approvalExpiresAt ?? undefined
        });
        setError("");
        toast.notify({
          tone: "success",
          title: ar ? "تم إرسال طلب الاسترداد للمدير" : "Refund approval request submitted",
          message: ar
            ? "لن يُنفذ الاسترداد قبل مراجعة المدير واعتماده."
            : "The refund will not execute until a manager reviews and approves it."
        });
        return;
      }
      if (!result.executed || !result.refund) {
        throw new Error("Refund orchestration returned no terminal result.");
      }

      setLastRefundDocument({ orderId: workflow.order.id, refundId: result.refund.id, refundNumber: result.refund.refundNumber });
      setRefunds(current => [result.refund!, ...current.filter(x => x.id !== result.refund!.id)]);
      if (result.refund.paymentAllocations.some(p => p.method === "GiftCard"))
        setLastGiftRefund({ orderId: workflow.order.id, refundId: result.refund.id });
      toast.notify({ tone: "success", title: ar ? "تم تنفيذ الاسترداد" : "Refund completed" });
      setRefundOpen(false);
      setRefundReason("");
      setRefundQuantities({});
      await loadWorkflow(workflow.order.id);
    } catch (caught) {
      const code = (caught as { problem?: { title?: string } }).problem?.title;
      if ([400,401,403,404,409,422].includes((caught as { status?: number }).status ?? 0)) setRefundPending(null);
      if (code === "REFUND_PREVIEW_CHANGED") { allocationPreview.refresh(); setError(ar ? "تغير الطلب أو توزيع الاسترداد. راجع المعاينة المحدّثة قبل التأكيد." : "The order or refund allocation changed. Review the refreshed preview before confirming."); return; }
      if (code === "GIFT_CARD_REPLACEMENT_REQUIRED") { setGiftReplacementRequired(true); return; }
      if (isPendingApprovalError(caught)) {
        setRefundOpen(false);
        setRefundReason("");
        setRefundQuantities({});
        setApprovalNotice({ operation: "Refund", duplicate: true });
        setError("");
        toast.notify({
          tone: "warning",
          title: ar ? "طلب الاسترجاع معلق بالفعل" : "Refund approval already pending",
          message: ar
            ? "لم يتم إنشاء طلب مكرر. يجب مراجعته من شاشة موافقات المدير."
            : "No duplicate was created. Review the existing request in Manager Approvals."
        });
        return;
      }
      handleError(caught);
    } finally { refundBusyRef.current = false; setRefundBusy(false); }
  };

  const closeShift = async () => {
    if (!shift) return;
    setShiftCloseError("");
    const countedCashMinor = majorInputToMinor(countedCash, shift.currency);
    if (countedCash.trim() === "" || countedCashMinor === null || countedCashMinor < 0) {
      setShiftCloseError(ar ? "أدخل النقد المعدود فعلياً قبل إغلاق الوردية." : "Enter the physically counted cash before closing the shift.");
      return;
    }
    try {
      const result = await closeCurrentShift(countedCashMinor, closingNote, shift.version);
      setShift(result);
      setClosedShiftSummary(result);
      setShiftDrawerOpen(false);
      setCountedCash("");
      setClosingNote("");
    } catch (caught) {
      setShiftCloseError(valueError(caught, language));
      handleError(caught);
      if ((caught as { problem?: { title?: string } }).problem?.title === "SHIFT_HAS_OPEN_ORDERS") {
        try { setShift(await getCurrentShiftSummary()); } catch (refreshError) { handleError(refreshError); }
      }
    }
  };

  const reopenClosedOrder = async () => {
    if (!workflow || !reopenReason.trim()) return;
    try {
      const correction = await reopenCashierClosedOrder(workflow, reopenReason.trim());
      setWorkflow(correction);
      hydrateContext(correction);
      setReopenDialogOpen(false);
      setReopenReason("");
      setContextOpen(true);
      await refreshReferenceData();
      toast.notify({
        tone: "success",
        title: ar ? "تم إنشاء طلب تصحيح" : "Correction order created",
        message: ar
          ? "بقيت المعاملة الأصلية المدفوعة محفوظة دون تعديل."
          : "The original paid transaction remains preserved and unchanged."
      });
    } catch (caught) {
      handleError(caught);
    }
  };

  const openPaidCustomerCorrection = () => {
    if (!workflow) return;
    hydrateContext(workflow);
    setPaidCustomerCorrectionReason("");
    setPaidCustomerCorrectionOpen(true);
  };

  const savePaidCustomerCorrection = async () => {
    if (!workflow) return;
    if (!paidCustomerCorrectionReason.trim()) {
      setError(
        ar
          ? "أدخل سبب تعديل بيانات العميل بعد الدفع؛ سيظهر في سجل التدقيق."
          : "Enter the reason for changing customer data after payment; it is recorded in the audit trail."
      );
      return;
    }

    const taxInvoiceError = taxInvoiceValidationMessage();
    if (taxInvoiceError) {
      setError(taxInvoiceError);
      return;
    }

    try {
      setBusy(true);
      const revised = await correctPaidCashierOrderCustomerData(workflow, {
        customerDataSourceId: contextCustomerId || null,
        contactName: contextContactName.trim() || null,
        contactPhone: contextContactPhone.trim() || null,
        taxInvoiceRequested: contextTaxInvoiceRequested,
        taxInvoiceCompanyName: contextTaxInvoiceRequested
          ? contextTaxInvoiceCompanyName.trim()
          : null,
        taxInvoiceTaxRegistrationNumber: contextTaxInvoiceRequested
          ? normalizedTaxInvoiceTrn()
          : null,
        taxInvoiceCompanyAddress: contextTaxInvoiceRequested
          ? contextTaxInvoiceAddress.trim()
          : null,
        reason: paidCustomerCorrectionReason.trim()
      });
      const returnToReceipt = receiptWorkflow?.order.id === revised.order.id;
      if (returnToReceipt) {
        setReceiptWorkflow(revised);
        clearActiveWorkspace();
      } else {
        setWorkflow(revised);
      }
      setReceipt(revised.receipt);
      setPaidCustomerCorrectionOpen(false);
      setPaidCustomerCorrectionReason("");
      await refreshReferenceData();
      toast.notify({
        tone: "success",
        title: ar
          ? "تم تحديث بيانات العميل وإعادة إصدار المستند"
          : "Customer data updated and document reissued",
        message: revised.receipt
          ? revised.receipt.receiptNumber + " · " + (ar ? "الإصدار" : "revision") + " " + revised.receipt.revisionNumber
          : undefined
      });
    } catch (caught) {
      handleError(caught);
    } finally {
      setBusy(false);
    }
  };

  const selectedCustomer = contextSelectedCustomer ?? customers.find((customer) => customer.id === contextCustomerId);

  if (busy && !catalog) {
    return (
      <div className="cashier-v2-loading">
        <Spinner label={ar ? "جارٍ التحميل" : "Loading"} />
      </div>
    );
  }

  const topBar = (
    <CashierTopBarV2
      key={`${context.tenantId}:${context.branchId}`}
      canSetGiftCardExpiry={can("giftcards.manage")}
      language={language}
      branchName={context.branchName}
      tenantName={context.tenantName}
      roleName={context.roleName}
      shiftStatus={shift?.status ?? null}
      shiftOpenedAt={shift?.openedAt ?? null}
      closedOrderCount={shift?.orderCount ?? 0}
      openOrderCount={orderCards.length}
      online={offline.online}
      pendingSyncCount={offline.pendingCount}
      queueRequiresReview={offline.requiresReview}
      queueErrorCode={offline.errorCode}
      syncing={offline.syncing}
      pendingApprovalCount={pendingApprovalCount}
      futureOrdersAvailable={can("preorders.read")}
      futureOrdersEnabled={Boolean(runtimeSettings?.preOrdersEnabled)}
      onOpenShift={() => {
        if (!shift) { setOpeningShiftDialogOpen(true); return; }
        setShiftCloseError(""); setShiftDrawerOpen(true);
        void getCurrentShiftSummary().then(setShift).catch(handleError);
      }}
      onOpenClosedOrders={() => undefined}
      onOpenSavedOrders={() => setSavedOrdersOpen(true)}
      onSync={() => void offline.sync()}
      onOpenFutureOrders={() => setFutureOrdersOpen(true)}
      onOpenManagerApprovals={onOpenManagerApprovals}
      onToggleLanguage={onToggleLanguage ?? (() => undefined)}
      onSwitchContext={onSwitchContext ?? (() => undefined)}
      onLogout={onLogout ?? onSessionExpired}
      onPrintClosedReceipt={(document, mode) => printReceipt(document, mode)}
      requiresManagerRefundApproval={runtimeSettings?.refundPolicy === "ManagerApproval"}
      onCorrectClosedOrderCustomer={can("orders.update") && can("receipts.reprint") ? (closedWorkflow) => {
        setWorkflow(closedWorkflow);
        setActivePreOrder(null);
        hydrateContext(closedWorkflow);
        setPaidCustomerCorrectionReason("");
        setPaidCustomerCorrectionOpen(true);
      } : undefined}
    />
  );

  const contextBar = workflow ? (
    <CashierContextBarV2
      identity={
        <>
          <StatusBadge tone="info">{valueLabel(workflow.order.serviceType, language)}</StatusBadge>
          <strong>{displayOrderNumber(workflow.order.orderNumber)}</strong>
        </>
      }
      customer={<span>{workflow.customer?.name ?? workflow.fulfillment.contactName ?? (ar ? "بدون عميل" : "No customer")}</span>}
      financialStatus={<StatusBadge tone={workflow.order.paymentStatus === "Paid" ? "success" : workflow.order.paymentStatus === "PartiallyPaid" ? "warning" : "neutral"}>{valueLabel(workflow.order.paymentStatus, language)}</StatusBadge>}
      preparationStatus={<span data-testid="preparation-status">{workflow.order.status === "Ready" ? (ar ? "التحضير · جاهز" : "Preparation · Ready") : workflow.order.status === "SentToKitchen" ? (ar ? "التحضير · جارٍ" : "Preparation · Preparing") : (ar ? "التحضير · لم يُرسل" : "Preparation · Not sent")}</span>}
      actions={
        <>
          <Button size="sm" variant="secondary" disabled={isClosedWorkflow} onClick={() => {
            if (activePreOrder) { setFutureOrdersOpen(true); return; }
            hydrateContext(workflow);
            setContextError("");
            setContextOpen(true);
          }}>{activePreOrder ? (ar ? "إدارة الحجز" : "Manage booking") : (ar ? "تعديل السياق" : "Edit Context")}</Button>
          <Button size="sm" variant="secondary" onClick={() => setNewOrderOpen(true)}>{ar ? "طلب جديد" : "New Order"}</Button>
          {canReviewPricing ?
            <Button size="sm" variant="secondary" data-testid="open-pricing-recovery" disabled={busy || paymentOpen || couponBusy}
              onClick={() => setPricingRecoveryOrderId(workflow.order.id)}>{ar ? "مراجعة التسعير" : "Review pricing"}</Button> : null}
        </>
      }
    />
  ) : (
    <CashierContextBarV2
      identity={<strong>{ar ? "لا يوجد طلب نشط" : "No active order"}</strong>}
      actions={<Button size="sm" data-testid="context-new-order" onClick={() => setNewOrderOpen(true)}>{ar ? "طلب جديد" : "New Order"}</Button>}
    />
  );

  const catalogPane = (
    <CashierCatalogV2
      language={language}
      catalog={catalog}
      products={workflow ? filteredProducts : catalog?.products ?? []}
      selectedCategoryId={selectedCategoryId}
      productSearch={productSearch}
      disabled={!workflow || isOrderItemEditingLocked}
      formatMoney={formatMoney}
      onSearchChange={setProductSearch}
      onSelectCategory={setSelectedCategoryId}
      onOpenProduct={openProduct}
    />
  );

  const cartLines = workflow?.cart.lines.map((line) => {
    const product = catalog?.products.find((item) => item.id === line.productId);
    const quantityStepMilli = product?.quantityStepMilli ?? 1000;
    return (
      <div className="cashier-v2-line" key={line.id}>
        <div className="cashier-v2-line__copy">
          <strong>{ar ? line.productNameAr : line.productNameEn}</strong>
          <small>{ar ? line.variantNameAr : line.variantNameEn}</small>
          {line.unitPriceBeforeTaxMinor != null ? <small>{ar ? "سعر الوحدة قبل الضريبة: " : "Unit price before tax: "}{formatMoney(line.unitPriceBeforeTaxMinor, workflow.order.currency)}</small> : null}
        </div>
        <strong className="cashier-v2-line__price">{formatMoney(line.totalMinor, workflow.order.currency)}</strong>
        <div className="cashier-v2-line__qty">
          <button type="button" title={preOrderReductionHelp} aria-label={preOrderReductionHelp ?? (ar ? "تنقيص الكمية" : "Decrease quantity")} disabled={isOrderItemEditingLocked} onClick={() => void changeLineQuantity(line, -quantityStepMilli)}>−</button>
          <span>{line.quantityMilli / 1000}</span>
          <button type="button" disabled={isOrderItemEditingLocked} onClick={() => void changeLineQuantity(line, quantityStepMilli)}>+</button>
          <button type="button" disabled={isOrderItemEditingLocked} onClick={() => openLineEditor(line)}>{ar ? "تعديل" : "Edit"}</button>
        </div>
      </div>
    );
  }) ?? [];

  const cartSummary = workflow ? (
    <div className="cashier-v2-summary" data-testid="cart-summary">
      {orderFinancialRows(workflow.cart.financialSummary, workflow.order, ar).map(row => (
        <div key={row.key} className={row.key === "total" ? "is-grand" : undefined} data-testid={row.key} data-amount-minor={row.amountMinor}>
          <span>{row.label}</span><strong data-testid={row.key === "total" ? "cart-total" : undefined}>{formatMoney(row.amountMinor, workflow.order.currency)}</strong>
        </div>
      ))}
      {workflow.order.discountMinor > 0 ? <div className="discount-details"><small>{ar ? "تفاصيل الخصم حسب أساس التسعير" : "Discount details on the pricing basis"}</small>
        {receiptDiscountSummary({ discountMinor: workflow.order.discountMinor, discountBreakdown: workflow.discountBreakdown }, ar).rows.map(row => (
          <div key={row.key} data-discount={row.key} data-amount-minor={row.amountMinor}>
            <span>{row.label}</span><strong dir="ltr">{formatMoney(-row.amountMinor, workflow.order.currency)}</strong>
          </div>
        ))}
      </div> : null}
      {Array.from(new Set(workflow.payments.map(payment => payment.method))).map(method => {
        const amount = workflow.payments.filter(payment => payment.method === method).reduce((sum, payment) => sum + payment.amountMinor, 0);
        return amount !== 0 ? <div key={method} data-payment-method={method} data-amount-minor={amount}>
          <span>{paymentMethodLabel(method)}</span><strong>{formatMoney(amount, workflow.order.currency)}</strong>
        </div> : null;
      })}
      <div className="is-paid"><span>{ar ? "إجمالي المدفوع" : "Total paid"}</span><strong data-testid="cart-paid">{formatMoney(workflow.order.amountPaidMinor)}</strong></div>
      {workflow.order.balanceDueMinor > 0 ? <div className="is-balance"><span>{ar ? "المتبقي" : "Balance"}</span><strong>{formatMoney(workflow.order.balanceDueMinor)}</strong></div> : null}
    </div>
  ) : null;

  const checkout = workflow && !isClosedWorkflow && workflow.order.balanceDueMinor > 0 && can("payments.take") ? (
    <Button
      block
      className="cashier-v2-pay"
      data-testid="open-payment"
      disabled={!workflow.cart.lines.length || !offline.online}
      onClick={() => void preparePayment()}
    >
      <span>{activePreOrder ? (ar ? "إدارة الدفع" : "Manage payment") : (ar ? "الدفع" : "Pay")}</span>
      <strong>{formatMoney(workflow.order.balanceDueMinor)}</strong>
    </Button>
  ) : null;

  const actions = workflow ? (
    <>
      <div className="cashier-v2-actions__primary">
        <Button
          className="cashier-action-save"
          data-testid="save-order"
          disabled={isClosedWorkflow}
          onClick={() => void saveCurrentOrder()}
        >
          {ar ? "حفظ الطلب" : "Save Order"}
        </Button>
        {activePreOrder ? (
          <Button className="cashier-action-hold" variant="secondary" onClick={() => setFutureOrdersOpen(true)}>
            {ar ? "إدارة الحجز" : "Manage booking"}
          </Button>
        ) : workflow.order.status === "Held" ? (
          <Button className="cashier-action-hold" variant="secondary" onClick={() => void resumeOrder()}>{ar ? "استئناف" : "Resume"}</Button>
        ) : (
          <Button className="cashier-action-hold" variant="secondary" disabled={isClosedWorkflow} onClick={() => setHoldOpen(true)}>{ar ? "تعليق" : "Hold"}</Button>
        )}
      </div>
      <div className="cashier-v2-actions__secondary">
        {can("orders.move") && !activePreOrder ? <>
          <Button data-testid="open-order-merge" variant="secondary" disabled={isClosedWorkflow || paymentOpen || !["Draft", "Held"].includes(workflow.order.status)}
            onClick={() => setOrderTransfer({ mode: "merge", orderId: workflow.order.id })}>{ar ? "دمج الطلبات" : "Merge orders"}</Button>
          <Button data-testid="open-order-split" variant="secondary" disabled={isClosedWorkflow || paymentOpen || !workflow.cart.lines.length || !["Draft", "Held"].includes(workflow.order.status)}
            onClick={() => setOrderTransfer({ mode: "split", orderId: workflow.order.id })}>{ar ? "تقسيم الطلب" : "Split order"}</Button>
        </> : null}
        {activePreOrder ? (
          <>
            <Button className="cashier-preparation-strip" variant="secondary" data-testid="send-order" onClick={() => setFutureOrdersOpen(true)}>
              {ar ? "إدارة الإرسال" : "Manage release"}
            </Button>
            <Button variant="secondary" onClick={() => setFutureOrdersOpen(true)}>{ar ? "إدارة التجهيز" : "Manage preparation"}</Button>
          </>
        ) : (
          <>
            <Button className="cashier-preparation-strip" variant="secondary" data-testid="send-order" disabled={isClosedWorkflow || !workflow.cart.lines.length} onClick={() => void sendOrder()}>{workflow.order.status === "SentToKitchen" || workflow.order.status === "Ready" ? (ar ? "تم الإرسال" : "Sent") : (ar ? "إرسال الطلب" : "Send Order")}</Button>
            <Button variant="secondary" disabled={isClosedWorkflow} onClick={() => void markReady()}>{ar ? "جاهز" : "Ready"}</Button>
          </>
        )}
        {(activePreOrder ? can("preorders.collect_deposit") || can("preorders.collect_balance") : can("payments.take")) ? <Button variant="secondary" data-testid="open-gift-card" disabled={isClosedWorkflow || workflow.order.balanceDueMinor <= 0} onClick={() => setGiftPaymentOpen(true)}>{ar ? "بطاقة هدية" : "Gift card"}</Button> : null}
        {can("discounts.apply") ? <Button className="cashier-action-discount" variant="secondary" disabled={isClosedWorkflow || isPreOrderCartLocked} onClick={() => setDiscountOpen(true)}>{ar ? "خصم" : "Discount"}</Button> : null}
        {can("discounts.apply") ? <Button variant="secondary" disabled={isClosedWorkflow || Boolean(activePreOrder && ["Fulfilled", "Cancelled"].includes(activePreOrder.summary.status)) || couponBusy} data-testid="open-coupon" onClick={() => { setCouponError(""); setCouponOpen(true); }}>{ar ? "قسيمة" : "Coupon"}</Button> : null}
        <Button variant="secondary" data-testid="print-active-order-prebill" disabled={isClosedWorkflow || !workflow.cart.lines.length || printingPreBillOrderId === workflow.order.id} onClick={() => void printPreBillForOrder(workflow.order.id, workflow.order.version)}>{printingPreBillOrderId === workflow.order.id ? (ar ? "جارٍ التجهيز…" : "Preparing…") : (ar ? "طباعة الفاتورة" : "Print invoice")}</Button>
        {can("orders.cancel") ? (
          <Button
            variant="danger"
            disabled={isClosedWorkflow || cancelBusy}
            onClick={() => {
              setCancelError("");
              if (activePreOrder) setFutureOrdersOpen(true);
              else if (workflow.cart.lines.length === 0) void cancelOrder();
              else setCancelOpen(true);
            }}
          >
            {activePreOrder ? (ar ? "إدارة الإلغاء" : "Manage cancellation") : (ar ? "إلغاء" : "Cancel")}
          </Button>
        ) : null}
        {can("refunds.create") && runtimeSettings?.refundPolicy !== "Disabled" && workflow.order.paymentStatus !== "Unpaid" ? (
          <Button variant="secondary" onClick={() => activePreOrder ? setFutureOrdersOpen(true) : setRefundOpen(true)}>
            {activePreOrder
              ? (ar ? "إدارة الاسترداد" : "Manage refund")
              : ar ? `استرداد${refunds.length ? ` (${refunds.length})` : ""}` : `Refund${refunds.length ? ` (${refunds.length})` : ""}`}
          </Button>
        ) : null}
      </div>
    </>
  ) : null;

  const cartPane = (
    <CashierCartV2
      language={language}
      header={workflow ? <><strong>{ar ? "سلة الطلب" : "Order cart"}</strong><span>{displayOrderNumber(workflow.order.orderNumber)}</span></> : undefined}
      lines={cartLines}
      summary={cartSummary}
      checkout={checkout}
      actions={actions}
      empty={!workflow}
      onNewOrder={() => setNewOrderOpen(true)}
    />
  );

  return (
    <>
      <CashierPresentationV2 topBar={topBar} contextBar={contextBar} catalog={catalogPane} cart={cartPane} />

      {couponNotice ? <div className="cashier-v2-global-alert"><Alert tone="info" title={ar ? "تحديث القسيمة" : "Coupon update"}>{couponNotice}</Alert></div> : null}
      {isClosedWorkflow ? (
        <div className="cashier-v2-global-alert cashier-v2-closed-original">
          <Alert tone="warning" title={ar ? "المعاملة الأصلية مغلقة ومحفوظة" : "Original transaction is closed and preserved"}>
            <Stack>
              <span>{ar ? "يمكن مراجعة السجل والاسترداد، لكن لا يمكن تعديل المعاملة المالية الأصلية." : "The original financial transaction is read-only. Review and refund remain available without mutating it."}</span>
              {canReopenWorkflow && can("orders.reopen") ? <Button data-testid="reopen-closed-order" onClick={() => setReopenDialogOpen(true)}>{ar ? "إنشاء طلب تصحيح" : "Create correction order"}</Button> : null}
              {workflow
                && workflow.order.paymentStatus === "Paid"
                && can("orders.update")
                && can("receipts.reprint") ? (
                <Button
                  data-testid="correct-paid-customer-data"
                  variant="secondary"
                  onClick={openPaidCustomerCorrection}
                >
                  {ar ? "تعديل بيانات العميل وإعادة إصدار المستند" : "Correct customer data & reissue document"}
                </Button>
              ) : null}
            </Stack>
          </Alert>
        </div>
      ) : null}

      {activePreOrder ? (
        <div className="cashier-v2-global-alert" data-testid="active-preorder-edit-guidance">
          {activePreOrder.summary.status === "Draft" ? (
            <Alert tone="info" title={ar ? "مرونة كاملة قبل التأكيد" : "Full flexibility before confirmation"}>
              {ar
                ? "قبل التأكيد يمكن إضافة الأصناف أو زيادتها أو تنقيصها أو حذفها مباشرة. تبدأ موافقة المدير فقط بعد التأكيد."
                : "Before confirmation, items may be added, increased, reduced or removed directly. Manager authorization starts only after confirmation."}
            </Alert>
          ) : (
            <Alert tone="info" title={ar ? "ضوابط التعديل بعد التأكيد" : "Post-confirmation item controls"}>
              {ar
                ? "يبقى هذا الطلب المستقبلي قابلاً للتعديل سواء تم تحصيل عربون أم لا. بعد التأكيد تبقى الإضافة والزيادة مباشرة، بينما يتطلب كل تنقيص أو حذف رمز مدير. وإذا بدأ التحضير، يرسل الحفظ تلقائياً تذكرة تعديل KOT محدثة."
                : "This active future order remains editable whether or not a deposit was collected. After confirmation, additions and increases remain direct; every reduction or removal requires a manager PIN. If preparation has started, saving automatically sends an updated amendment KOT."}
            </Alert>
          )}
        </div>
      ) : null}

      {error ? <div className="cashier-v2-global-alert"><Alert tone="danger" title={ar ? "تعذر تنفيذ العملية" : "Operation failed"} onDismiss={() => setError("")}>{error}</Alert></div> : null}

      {approvalNotice ? (
        <div className="cashier-v2-global-alert cashier-v2-approval-notice">
          <Alert
            tone="info"
            title={
              approvalNotice.duplicate
                ? ar ? "يوجد طلب موافقة معلق بالفعل" : "An approval request is already pending"
                : approvalNotice.operation === "Refund"
                  ? ar ? "تم إرسال طلب الاسترداد" : "Refund request sent"
                  : ar ? "تم إرسال طلب الإهلاك" : "Void request sent"
            }
            onDismiss={() => setApprovalNotice(null)}
          >
            <Stack>
              <span>
                {ar
                  ? "لم تتغير حالة الطلب بعد. يجب أن يسجل مدير آخر الدخول، ثم يفتح «موافقات المدير» ويختار الموافقة والتنفيذ أو الرفض."
                  : "The order has not changed yet. A different manager must sign in, open Manager Approvals, and approve & execute or reject the request."}
              </span>
              {approvalNotice.expiresAt ? (
                <small>
                  {ar ? "تنتهي صلاحية الطلب: " : "Request expires: "}
                  {new Date(approvalNotice.expiresAt).toLocaleString(ar ? "ar-AE" : "en-AE")}
                </small>
              ) : null}
              {onOpenManagerApprovals ? (
                <Inline>
                  <Button size="sm" variant="secondary" onClick={onOpenManagerApprovals}>
                    {ar ? "فتح موافقات المدير" : "Open Manager Approvals"}
                  </Button>
                </Inline>
              ) : null}
            </Stack>
          </Alert>
        </div>
      ) : null}

      <Dialog
        open={openingShiftDialogOpen}
        onClose={() => setOpeningShiftDialogOpen(false)}
        title={ar ? "فتح الوردية" : "Open shift"}
        footer={<Inline><Button variant="secondary" onClick={() => setOpeningShiftDialogOpen(false)}>{ar ? "إلغاء" : "Cancel"}</Button><Button data-testid="confirm-open-shift" disabled={!offline.online || openingCash.trim() === ""} onClick={() => void openNewShift()}>{ar ? "فتح الوردية" : "Open shift"}</Button></Inline>}
      >
        <Stack>
          <Alert tone="warning" title={ar ? "العهدة الافتتاحية إلزامية" : "Opening float is mandatory"}>
            {ar ? "أدخل الرصيد النقدي الفعلي في الدرج قبل أول عملية بيع، ويمكن إدخال 0 عندما يكون الدرج فارغاً." : "Enter the actual cash in the drawer before the first sale; enter 0 when the drawer is empty."}
          </Alert>
          <Field data-testid="opening-cash" label={ar ? "العهدة الافتتاحية" : "Opening float"} type="number" min={0} step={0.01} value={openingCash} onChange={(event) => setOpeningCash(event.target.value)} />
        </Stack>
      </Dialog>

      <Drawer open={shiftDrawerOpen} onClose={() => setShiftDrawerOpen(false)} title={ar ? "إدارة الوردية" : "Shift management"}>
        {shift?.status === "Open" ? (
          <Stack>
            <CashierShiftSummaryV2 summary={shift} language={language} formatMoney={formatMoney} />
            <Card title={ar ? "الطلبات المانعة لإغلاق الوردية" : "Orders blocking shift closure"}>
              <Button variant="secondary" onClick={() => void getCurrentShiftSummary().then(setShift).catch(handleError)}>{ar ? "تحديث الطلبات المانعة" : "Refresh blocking orders"}</Button>
              {(shift.blockingOrders?.length ?? 0) > 0 ? <>
                <p>{ar ? "هذه طلبات الوردية الحالية وفق شروط الإغلاق، وليست كل الطلبات الظاهرة في قائمة الكاشير. حالة الدفع وحدها لا تنهي حالة الطلب التشغيلية." : "These are the current shift’s orders matched by the closing guard, not the cashier’s full order list. Payment status alone does not complete an operational order."}</p>
                <ul data-testid="shift-blocking-orders">{shift.blockingOrders!.map(order => <li key={order.orderId}>
                  <Button variant="secondary" onClick={() => { setShiftDrawerOpen(false); void loadWorkflow(order.orderId); }}><bdi>#{order.orderNumber}</bdi></Button>
                  <span> · {valueLabel(order.status, language)} · {valueLabel(order.paymentStatus, language)} · {ar ? "التحضير: " : "Preparation: "}{valueLabel(order.fulfillmentStatus, language)}</span>
                </li>)}</ul>
              </> : null}
            </Card>
            <Card title={ar ? "مطابقة الصندوق" : "Cash drawer reconciliation"}>
              <Stack>
                <div><span>{ar ? "الرصيد الافتتاحي" : "Opening float"}</span><strong>{formatMoney(shift.openingCashMinor, shift.currency)}</strong></div>
                <div><span>{ar ? "النقد المتوقع" : "Expected cash"}</span><strong>{formatMoney(shift.expectedCashMinor, shift.currency)}</strong></div>
                <Field data-testid="counted-cash" label={ar ? "النقد المعدود (" + shift.currency + ")" : "Counted cash (" + shift.currency + ")"} type="number" min={0} step={0.01} value={countedCash} onChange={(event) => setCountedCash(event.target.value)} />
                <TextAreaField label={ar ? "ملاحظة الإغلاق" : "Closing note"} value={closingNote} onChange={(event) => setClosingNote(event.target.value)} />
                {shiftCloseError ? <Alert tone="danger" title={ar ? "تعذر إغلاق الوردية" : "Shift could not be closed"}><span role="alert">{shiftCloseError}</span></Alert> : null}
                <Button data-testid="close-shift" variant="danger" disabled={!offline.online} onClick={() => void closeShift()}>{ar ? "إغلاق الوردية" : "Close shift"}</Button><small data-testid="close-shift-guidance">{!offline.online ? (ar ? "يتطلب إغلاق الوردية اتصالاً بالشبكة." : "Closing the shift requires connectivity.") : countedCash.trim() === "" ? (ar ? "أدخل النقد المعدود ثم اضغط إغلاق الوردية." : "Enter counted cash, then close the shift.") : (ar ? "سيتم التحقق من عدم وجود طلبات تشغيلية مفتوحة قبل الإغلاق." : "Open operational orders are validated before closure.")}</small>
              </Stack>
            </Card>
          </Stack>
        ) : shift?.status === "Closed" ? (
          <Stack>
            <Alert tone="success" title={ar ? "الوردية مغلقة" : "Shift closed"}>
              {ar ? "تم تثبيت الأرقام النهائية للوردية. يمكنك إعادة طباعة تقرير الإغلاق." : "Final shift figures are locked. You can reprint the closure report."}
            </Alert>
            <Button data-testid="reprint-shift-z-report" onClick={() => printShiftClosureReport(shift)}>
              {ar ? "إعادة طباعة تقرير إغلاق الوردية" : "Reprint shift Z-report"}
            </Button>
            <Button variant="secondary" onClick={() => setOpeningShiftDialogOpen(true)}>
              {ar ? "فتح وردية جديدة" : "Open new shift"}
            </Button>
          </Stack>
        ) : (
          <EmptyState icon="activity" title={ar ? "لا توجد وردية" : "No shift"} />
        )}
      </Drawer>

      {workflow && pricingRecoveryOrderId === workflow.order.id && canReviewPricing ?
        <CashierPricingRecoveryDialog key={`${workflow.order.id}:${workflow.order.version}`} workflow={workflow} language={language}
          couponOnly={Boolean(activePreOrder)}
          onSessionExpired={onSessionExpired} onClose={() => setPricingRecoveryOrderId(null)}
          onApplied={updated => {
            if (liveWorkflow.current?.order.id === workflow.order.id && liveWorkflow.current.order.version === workflow.order.version) {
              acceptCouponWorkflow(updated); setCouponNotice(""); setError(""); setPricingRecoveryOrderId(null);
              void refreshOperationalCards().catch(handleError);
            }
          }} /> : null}

      <Dialog
        open={Boolean(closedShiftSummary)}
        onClose={() => setClosedShiftSummary(null)}
        title={ar ? "ملخص إغلاق الوردية" : "Shift closing summary"}
        footer={
          <Inline>
            <Button variant="secondary" onClick={() => setClosedShiftSummary(null)}>{ar ? "تم" : "Done"}</Button>
            {closedShiftSummary ? (
              <Button data-testid="print-shift-z-report" onClick={() => printShiftClosureReport(closedShiftSummary)}>
                {ar ? "طباعة تقرير الوردية" : "Print Z-report"}
              </Button>
            ) : null}
          </Inline>
        }
      >
        {closedShiftSummary ? (
          <Stack>
            <CashierShiftSummaryV2 summary={closedShiftSummary} language={language} formatMoney={formatMoney} />
            <Card title={ar ? "مطابقة الصندوق النهائية" : "Final cash reconciliation"}>
              <Grid columns={3}>
                <div><span>{ar ? "النقد المتوقع" : "Expected cash"}</span><strong>{formatMoney(closedShiftSummary.expectedCashMinor, closedShiftSummary.currency)}</strong></div>
                <div><span>{ar ? "النقد المعدود" : "Counted cash"}</span><strong>{formatMoney(closedShiftSummary.countedCashMinor ?? 0, closedShiftSummary.currency)}</strong></div>
                <div><span>{ar ? "فرق الصندوق" : "Variance"}</span><strong>{formatMoney(closedShiftSummary.varianceMinor ?? 0, closedShiftSummary.currency)}</strong></div>
              </Grid>
              {closedShiftSummary.closingNote ? <small>{ar ? "ملاحظة الإغلاق: " : "Closing note: "}{closedShiftSummary.closingNote}</small> : null}
            </Card>
          </Stack>
        ) : null}
      </Dialog>


      {workflow && orderTransfer?.orderId === workflow.order.id && !activePreOrder && can("orders.move") ?
        <CashierOrderTransferDialog key={`${workflow.order.id}:${workflow.order.version}`} mode={orderTransfer.mode}
          workflow={workflow} orders={orderCards} serviceTypes={runtimeSettings?.enabledServiceTypes ?? serviceTypes}
          tables={availableTables} language={language} onClose={() => setOrderTransfer(null)}
          onCompleted={result => {
            if (liveWorkflow.current?.order.id !== orderTransfer.orderId) return;
            setWorkflow(result); hydrateContext(result); setOrderTransfer(null); setError("");
            void refreshReferenceData().catch(handleError);
          }} /> : null}

      <Drawer open={newOrderOpen} onClose={() => setNewOrderOpen(false)} title={ar ? "طلب جديد" : "New order"}>
        <form onSubmit={createNewOrder}>
          <Stack>
            {runtimeSettings?.preOrdersEnabled ? (
              <Inline>
                <Button type="button" size="sm" variant={newOrderTiming === "Now" ? "primary" : "secondary"} onClick={() => setNewOrderTiming("Now")}>
                  {ar ? "الآن" : "Now"}
                </Button>
                <Button type="button" size="sm" variant={newOrderTiming === "Future" ? "primary" : "secondary"} onClick={() => setNewOrderTiming("Future")}>
                  {ar ? "طلب مستقبلي" : "Future order"}
                </Button>
              </Inline>
            ) : null}
            <div className="cashier-choice-block cashier-service-type-choice" data-testid="new-order-service-type-choices">
              <span className="cashier-choice-label">{ar ? "نوع الطلب" : "Service type"}</span>
              <div className="cashier-choice-compat-select-wrap" aria-hidden="true">
                <SelectField data-testid="new-order-service-type" label={ar ? "نوع الطلب" : "Service type"} value={newServiceType} onChange={(event) => setNewServiceType(event.target.value as ServiceType)}>
                  {(runtimeSettings?.enabledServiceTypes ?? serviceTypes).map((type) => <option key={type} value={type}>{type}</option>)}
                </SelectField>
              </div>
              <div className="cashier-choice-grid cashier-service-type-grid">
                {(runtimeSettings?.enabledServiceTypes ?? serviceTypes).map((type) => (
                  <Button
                    key={type}
                    type="button"
                    size="sm"
                    variant={newServiceType === type ? "primary" : "secondary"}
                    data-testid={`new-order-service-choice-${type}`}
                    onClick={() => setNewServiceType(type)}
                  >
                    {serviceTypeLabels[type][ar ? "ar" : "en"]}
                  </Button>
                ))}
              </div>
            </div>
            {newServiceType === "DineIn" ? <Grid columns={2}><SelectField data-testid="new-order-table" label={ar ? "الطاولة" : "Table"} value={newTableId} onChange={(event) => setNewTableId(event.target.value)}><option value="">{ar ? "اختر طاولة" : "Select table"}</option>{availableTables.map((table) => <option key={table.id} value={table.id}>{ar ? table.nameAr : table.nameEn}</option>)}</SelectField><Field data-testid="new-order-guests" label={ar ? "الضيوف" : "Guests"} type="number" min={1} value={newGuestCount} onChange={(event) => setNewGuestCount(event.target.value)} /></Grid> : null}
            {newServiceType === "Delivery" ? <Grid columns={2}><SelectField data-testid="new-order-delivery-customer" label={ar ? "عميل التوصيل" : "Delivery customer"} value={newDeliveryCustomerId} onChange={(event) => { setNewDeliveryCustomerId(event.target.value); setNewDeliveryAddressId(""); }}><option value="">{ar ? "اختر عميلاً" : "Select customer"}</option>{customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.name}</option>)}</SelectField><SelectField data-testid="new-order-delivery-address" label={ar ? "العنوان" : "Address"} value={newDeliveryAddressId} onChange={(event) => setNewDeliveryAddressId(event.target.value)}><option value="">{ar ? "اختر عنواناً" : "Select address"}</option>{(customers.find((customer) => customer.id === newDeliveryCustomerId)?.addresses ?? []).map((address) => <option key={address.id} value={address.id}>{address.label} · {address.addressLine1}</option>)}</SelectField></Grid> : null}
            {newOrderTiming === "Future" ? (
              <Card title={ar ? "جدولة الطلب المستقبلي" : "Future order schedule"}>
                <Stack>
                  {can("customers.read") ? <PreOrderCustomerPicker language={language} customer={newFutureCustomer}
                    canCreate={can("customers.manage")} onBusyChange={setNewFutureCustomerBusy}
                    onSelect={customer => { setNewFutureCustomer(customer); setNewFutureContactName(customer?.name ?? ""); setNewFutureContactPhone(customer?.phone ?? ""); }} /> : null}
                  <Grid columns={2}>
                    <Field label={ar ? "تاريخ ووقت الاستلام" : "Fulfillment date & time"} type="datetime-local" value={newFulfillmentAt} onChange={(event) => setNewFulfillmentAt(event.target.value)} />
                    <Field label={ar ? "دقائق التجهيز" : "Preparation lead minutes"} type="number" min={0} value={newPreparationLeadMinutes} onChange={(event) => setNewPreparationLeadMinutes(event.target.value)} />
                  </Grid>
                  <Grid columns={2}>
                    <Field label={ar ? "اسم العميل" : "Customer name"} value={newFutureContactName} onChange={(event) => setNewFutureContactName(event.target.value)} />
                    <Field label={ar ? "هاتف العميل" : "Customer phone"} value={newFutureContactPhone} onChange={(event) => setNewFutureContactPhone(event.target.value)} />
                    <Field label={ar ? "رقم المركبة" : "Vehicle number"} value={newFutureVehicleNumber} onChange={(event) => setNewFutureVehicleNumber(event.target.value)} />
                    <div>
                      <Field data-testid="new-preorder-optional-deposit" label={ar ? "مبلغ العربون الاختياري" : "Optional deposit amount"} type="number" min={0} step={0.01} inputMode="decimal" value={newFutureDepositTarget} onChange={(event) => setNewFutureDepositTarget(event.target.value)} />
                      <small>{ar ? "أدخل أي مبلغ ثابت أو 0 لتأكيد الطلب بدون عربون." : "Enter any fixed amount, or 0 to confirm without a deposit."}</small>
                    </div>
                  </Grid>
                  <TextAreaField data-testid="new-preorder-address-notes" label={ar ? "تفاصيل العنوان / الموقع" : "Address / location notes"} value={newFutureAddressNotes} onChange={(event) => setNewFutureAddressNotes(event.target.value)} />
                </Stack>
              </Card>
            ) : null}
            <Field data-testid="new-order-note" label={ar ? "ملاحظة" : "Note"} value={newOrderNote} onChange={(event) => setNewOrderNote(event.target.value)} />
            <Button data-testid="create-order" type="submit" disabled={newFutureCustomerBusy}>{ar ? "بدء الطلب" : "Start order"}</Button>
          </Stack>
        </form>
      </Drawer>

      <FutureOrdersDrawer
        open={futureOrdersOpen}
        onClose={() => setFutureOrdersOpen(false)}
        language={language}
        permissions={permissions}
        onOpenPreOrder={(detail) => {
          setActivePreOrder(detail);
          setWorkflow(detail.workflow);
          hydrateContext(detail.workflow);
          setContextOpen(false);
        }}
        onReceipt={(nextReceipt) => setReceipt(nextReceipt)}
        onChanged={() => void refreshReferenceData()}
      />

      <Drawer className="cashier-saved-orders-drawer" open={savedOrdersOpen} onClose={() => setSavedOrdersOpen(false)} title={ar ? "الطلبات المحفوظة والمفتوحة" : "Saved & open orders"}>
        <Stack data-testid="saved-orders-drawer">
          <Field data-testid="saved-orders-search" label={ar ? "بحث" : "Search"} value={savedOrdersSearch} onChange={(event) => setSavedOrdersSearch(event.target.value)} />
          <Inline>{(["All", "WalkIn", "Takeaway", "DineIn", "Delivery", "Held", "Preparation"] as SavedOrderFilter[]).map((filter) => <Button key={filter} size="sm" variant={savedOrdersFilter === filter ? "primary" : "secondary"} data-testid={`saved-orders-filter-${filter.toLowerCase()}`} onClick={() => setSavedOrdersFilter(filter)}>{filter}</Button>)}</Inline>
          <div className="cashier-order-list" data-testid="open-orders-list">
            {visibleSavedOrders.map((order) => <CashierOpenOrderCard key={order.orderId} language={language} order={order} active={workflow?.order.id === order.orderId} formatMoney={formatMoney} onSelect={() => { setSavedOrdersOpen(false); void loadWorkflow(order.orderId); }} onPrintPreBill={() => void printPreBillForOrder(order.orderId, order.orderVersion)} printingPreBill={printingPreBillOrderId === order.orderId} />)}
          </div>
        </Stack>
      </Drawer>

      <Drawer open={contextOpen} onClose={() => setContextOpen(false)} title={ar ? "سياق الطلب" : "Order context"} footer={<Inline><Button variant="secondary" onClick={() => setContextOpen(false)}>{ar ? "إلغاء" : "Cancel"}</Button><Button data-testid="save-order-context" onClick={() => void saveOrderContext()}>{ar ? "حفظ السياق" : "Save context"}</Button></Inline>}>
        {workflow ? <Stack>
          {contextError ? <Alert tone="danger" title={ar ? "تعذر حفظ العميل والسياق" : "Customer and context could not be saved"}><span role="alert" data-testid="order-context-error">{contextError}</span></Alert> : null}
          <SelectField data-testid="change-order-service-type" label={ar ? "نوع الطلب" : "Service type"} value={contextServiceType} onChange={(event) => setContextServiceType(event.target.value as ServiceType)}>{(runtimeSettings?.enabledServiceTypes ?? serviceTypes).map((type) => <option key={type} value={type}>{type}</option>)}</SelectField>
          <CashierCustomerSearchPicker
            language={language}
            label={ar ? "العميل" : "Customer"}
            value={contextCustomerId}
            initialCustomers={customers}
            selectedCustomer={selectedCustomer}
            onSelect={selectContextCustomerRecord}
            emptyLabel={ar ? "بدون عميل" : "No customer"}
            searchTestId="order-context-customer-search"
            selectTestId="order-context-customer"
            enabled={can("customers.read")}
          />
          {contextServiceType === "DineIn" ? <Grid columns={2}><SelectField data-testid="order-context-table" label={ar ? "الطاولة" : "Table"} value={contextTableId} onChange={(event) => setContextTableId(event.target.value)}><option value="">{ar ? "اختر طاولة" : "Select table"}</option>{availableTables.map((table) => <option key={table.id} value={table.id}>{ar ? table.nameAr : table.nameEn}</option>)}</SelectField><Field data-testid="order-context-guests" label={ar ? "الضيوف" : "Guests"} type="number" min={1} value={contextGuestCount} onChange={(event) => setContextGuestCount(event.target.value)} /></Grid> : null}
          {contextServiceType === "DineIn" ? <Grid columns={2}>
            <Field data-testid="order-context-contact-name" label={ar ? "اسم الضيف (اختياري)" : "Guest name (optional)"} value={contextContactName} onChange={event => setContextContactName(event.target.value)} />
            <Field data-testid="order-context-contact-phone" label={ar ? "هاتف الضيف (اختياري)" : "Guest phone (optional)"} value={contextContactPhone} onChange={event => setContextContactPhone(event.target.value)} />
          </Grid> : null}
          {contextServiceType === "Takeaway" || contextServiceType === "WalkIn" ? (
            <Card title={ar ? "بيانات الاستلام والعميل" : "Pickup & customer details"}>
              <Stack>
                <Grid columns={2}><Field data-testid="order-context-contact-name" label={ar ? "اسم العميل" : "Customer name"} value={contextContactName} onChange={(event) => setContextContactName(event.target.value)} /><Field data-testid="order-context-contact-phone" label={ar ? "الهاتف" : "Phone"} value={contextContactPhone} onChange={(event) => setContextContactPhone(event.target.value)} /></Grid>
                <Field data-testid="order-context-vehicle-number" label={ar ? "المركبة" : "Vehicle"} value={contextVehicleNumber} onChange={(event) => setContextVehicleNumber(event.target.value)} />
              </Stack>
            </Card>
          ) : null}
          {contextServiceType === "Delivery" ? <Card title={ar ? "بيانات التوصيل" : "Delivery details"}><Stack><Grid columns={2}><Field data-testid="order-context-contact-name" label={ar ? "اسم المستلم" : "Recipient name"} value={contextContactName} onChange={(event) => setContextContactName(event.target.value)} /><Field data-testid="order-context-contact-phone" label={ar ? "هاتف المستلم" : "Recipient phone"} value={contextContactPhone} onChange={(event) => setContextContactPhone(event.target.value)} /></Grid><SelectField data-testid="order-context-delivery-address" label={ar ? "عنوان التوصيل" : "Delivery address"} value={contextAddressId} onChange={(event) => setContextAddressId(event.target.value)}><option value="">{ar ? "اختر عنواناً" : "Select address"}</option>{contextAddressId && !selectedCustomer?.addresses.some((address) => address.id === contextAddressId) ? <option value={contextAddressId}>{ar ? "عنوان التوصيل المحفوظ بالطلب" : "Delivery address saved on order"}</option> : null}{selectedCustomer?.addresses.map((address) => <option key={address.id} value={address.id}>{address.label} · {address.addressLine1}</option>)}</SelectField><Field label={ar ? "رسوم التوصيل" : "Delivery fee"} type="number" value={contextDeliveryFee} onChange={(event) => setContextDeliveryFee(event.target.value)} /><TextAreaField label={ar ? "تعليمات التوصيل" : "Delivery instructions"} value={contextInstructions} onChange={(event) => setContextInstructions(event.target.value)} /><small data-testid="delivery-context-optional-note">{ar ? "اسم المستلم والهاتف والعنوان والموقع اختيارية بالكامل، ولا تمنع الدفع أو إغلاق الطلب." : "Recipient, phone, address and location are fully optional and never block payment or order closure."}</small></Stack></Card> : null}
          <Field label={ar ? "مرجع خارجي" : "External reference"} value={contextExternalReference} onChange={(event) => setContextExternalReference(event.target.value)} />
          <Card title={ar ? "الفاتورة الضريبية" : "Tax invoice"}><Stack><Checkbox data-testid="order-tax-invoice-requested" label={ar ? "إصدار فاتورة ضريبية" : "Issue tax invoice"} checked={contextTaxInvoiceRequested} onChange={(event) => setContextTaxInvoiceRequested(event.target.checked)} />{contextTaxInvoiceRequested ? <><Grid columns={2}><Field data-testid="tax-invoice-company-name" label={ar ? "اسم الشركة" : "Company legal name"} value={contextTaxInvoiceCompanyName} onChange={(event) => setContextTaxInvoiceCompanyName(event.target.value)} /><Field data-testid="tax-invoice-trn" label="TRN" value={contextTaxInvoiceTrn} onChange={(event) => setContextTaxInvoiceTrn(event.target.value)} /></Grid><TextAreaField data-testid="tax-invoice-company-address" label={ar ? "عنوان الشركة" : "Company address"} value={contextTaxInvoiceAddress} onChange={(event) => setContextTaxInvoiceAddress(event.target.value)} /></> : null}</Stack></Card>
        </Stack> : null}
      </Drawer>

      <Dialog open={postSaveDetailsOpen} onClose={() => setPostSaveDetailsOpen(false)} title={ar ? "تفاصيل العميل الاختيارية" : "Optional customer details"} footer={<Inline><Button variant="secondary" data-testid="post-save-skip-details" onClick={() => { setPostSaveDetailsOpen(false); clearActiveWorkspace(); }}>{ar ? "تخطي الآن" : "Skip for now"}</Button><Button data-testid="post-save-save-details" onClick={() => void saveOrderContext(false, true)}>{ar ? "حفظ التفاصيل" : "Save details"}</Button></Inline>}>
        {contextError ? <Alert tone="danger" title={ar ? "تعذر حفظ التفاصيل" : "Details could not be saved"}><span role="alert">{contextError}</span></Alert> : null}
        <Stack data-testid="post-save-customer-details"><Grid columns={2}><Field data-testid="post-save-customer-name" label={ar ? "الاسم" : "Name"} value={contextContactName} onChange={(event) => setContextContactName(event.target.value)} /><Field data-testid="post-save-customer-phone" label={ar ? "الهاتف" : "Phone"} value={contextContactPhone} onChange={(event) => setContextContactPhone(event.target.value)} /><Field data-testid="post-save-vehicle" label={ar ? "المركبة" : "Vehicle"} value={contextVehicleNumber} onChange={(event) => setContextVehicleNumber(event.target.value)} /><Field data-testid="post-save-reference" label={ar ? "المرجع" : "Reference"} value={contextExternalReference} onChange={(event) => setContextExternalReference(event.target.value)} /></Grid></Stack>
      </Dialog>

      <Dialog open={reopenDialogOpen} onClose={() => setReopenDialogOpen(false)} title={ar ? "استعادة المعاملة عبر طلب تصحيح" : "Restore transaction with a correction order"} footer={<Inline><Button variant="secondary" onClick={() => setReopenDialogOpen(false)}>{ar ? "إلغاء" : "Cancel"}</Button><Button data-testid="confirm-reopen-order" disabled={!reopenReason.trim()} onClick={() => void reopenClosedOrder()}>{ar ? "إنشاء طلب التصحيح" : "Create correction order"}</Button></Inline>}>
        <Stack><Alert tone="info" title={ar ? "حماية السجل المالي" : "Financial record protection"}>{ar ? "لن يُعاد فتح الإيصال المدفوع نفسه؛ سيُنشأ طلب جديد مرتبط به." : "The paid receipt itself is not reopened; a new linked correction order is created."}</Alert><TextAreaField data-testid="reopen-order-reason" label={ar ? "سبب التصحيح" : "Correction reason"} required value={reopenReason} onChange={(event) => setReopenReason(event.target.value)} /></Stack>
      </Dialog>

      <Dialog
        open={paidCustomerCorrectionOpen}
        onClose={() => setPaidCustomerCorrectionOpen(false)}
        title={ar ? "تعديل بيانات العميل بعد الدفع" : "Correct customer data after payment"}
        footer={
          <Inline>
            <Button variant="secondary" onClick={() => setPaidCustomerCorrectionOpen(false)}>
              {ar ? "إلغاء" : "Cancel"}
            </Button>
            <Button
              data-testid="save-paid-customer-correction"
              disabled={!paidCustomerCorrectionReason.trim()}
              onClick={() => void savePaidCustomerCorrection()}
            >
              {ar ? "حفظ وإعادة إصدار المستند" : "Save & reissue document"}
            </Button>
          </Inline>
        }
      >
        <Stack>
          <Alert
            tone="warning"
            title={ar ? "السجل المالي يبقى ثابتاً" : "The financial record remains immutable"}
          >
            {ar
              ? "يُستخدم العميل المحدد كمصدر لبيانات الهوية والفاتورة فقط؛ ولا يتغير مالك المحفظة أو الحساب الآجل أو الاسترداد."
              : "The selected customer is used only as the identity source; the wallet/account and refund owner is not changed."}
          </Alert>
          <CashierCustomerSearchPicker
            language={language}
            label={ar ? "استخدام هوية عميل محفوظ" : "Use saved customer identity"}
            value={contextCustomerId}
            initialCustomers={customers}
            selectedCustomer={selectedCustomer}
            onSelect={(customer) => {
              selectContextCustomerRecord(customer);
              if (customer) setContextTaxInvoiceRequested(true);
            }}
            emptyLabel={ar ? "بدون عميل محفوظ" : "No saved customer"}
            searchTestId="paid-correction-customer-search"
            selectTestId="paid-correction-saved-customer"
            enabled={can("customers.read")}
          />
          <Grid columns={2}>
            <Field
              label={ar ? "اسم العميل" : "Customer name"}
              value={contextContactName}
              onChange={(event) => setContextContactName(event.target.value)}
            />
            <Field
              label={ar ? "الهاتف" : "Phone"}
              value={contextContactPhone}
              onChange={(event) => setContextContactPhone(event.target.value)}
            />
          </Grid>
          <Card title={ar ? "بيانات الفاتورة الضريبية" : "Tax invoice identity"}>
            <Stack>
              <Checkbox
                label={ar ? "إصدار فاتورة ضريبية رسمية" : "Issue official tax invoice"}
                checked={contextTaxInvoiceRequested}
                onChange={(event) => setContextTaxInvoiceRequested(event.target.checked)}
              />
              {contextTaxInvoiceRequested ? (
                <>
                  <Grid columns={2}>
                    <Field
                      label={ar ? "اسم الشركة" : "Company legal name"}
                      value={contextTaxInvoiceCompanyName}
                      onChange={(event) => setContextTaxInvoiceCompanyName(event.target.value)}
                    />
                    <Field
                      label="TRN"
                      value={contextTaxInvoiceTrn}
                      onChange={(event) => setContextTaxInvoiceTrn(event.target.value.replace(/\D/g, "").slice(0, 15))}
                    />
                  </Grid>
                  <TextAreaField
                    label={ar ? "عنوان الشركة" : "Company address"}
                    value={contextTaxInvoiceAddress}
                    onChange={(event) => setContextTaxInvoiceAddress(event.target.value)}
                  />
                </>
              ) : null}
            </Stack>
          </Card>
          <TextAreaField
            data-testid="paid-customer-correction-reason"
            label={ar ? "سبب التعديل وإعادة الإصدار" : "Correction & reissue reason"}
            required
            value={paidCustomerCorrectionReason}
            onChange={(event) => setPaidCustomerCorrectionReason(event.target.value)}
          />
        </Stack>
      </Dialog>

      <Dialog
        open={Boolean(pendingPreOrderReduction)}
        onClose={() => {
          setPendingPreOrderReduction(null);
          setManagerReductionPin("");
          setManagerReductionReason("");
          setManagerReductionError("");
        }}
        title={ar ? "تفويض مدير لتنقيص أو حذف صنف" : "Manager authorization for reduction or removal"}
        footer={
          <Inline>
            <Button variant="secondary" onClick={() => {
                setPendingPreOrderReduction(null);
                setManagerReductionPin("");
                setManagerReductionReason("");
                setManagerReductionError("");
              }}>{ar ? "إلغاء" : "Cancel"}</Button>
            <Button data-testid="submit-preorder-manager-reduction" onClick={() => void submitPreOrderManagerReduction()}>
              {pendingPreOrderReduction?.targetQuantityMilli === 0
                ? ar ? "اعتماد الحذف" : "Authorize removal"
                : ar ? "اعتماد التنقيص" : "Authorize reduction"}
            </Button>
          </Inline>
        }
      >
        {pendingPreOrderReduction ? (
          <Stack>
            <Alert tone="warning" title={pendingPreOrderReduction.targetQuantityMilli === 0 ? (ar ? "حذف صنف محمي" : "Protected item removal") : (ar ? "تنقيص كمية محمي" : "Protected quantity reduction")}>
              {pendingPreOrderReduction.lineLabel} · {pendingPreOrderReduction.currentQuantityMilli / 1000} → {pendingPreOrderReduction.targetQuantityMilli / 1000}
            </Alert>
            <SecretField
              data-testid="preorder-manager-reduction-pin"
              label={ar ? "رمز المدير" : "Manager PIN"}
              revealLabel={ar ? "إظهار رمز المدير" : "Show manager PIN"}
              concealLabel={ar ? "إخفاء رمز المدير" : "Hide manager PIN"}
              inputMode="numeric"
              autoComplete="off"
              value={managerReductionPin}
              onChange={(event) => setManagerReductionPin(event.target.value.replace(/\D/g, "").slice(0, 8))}
            />
            <TextAreaField
              data-testid="preorder-manager-reduction-reason"
              label={ar ? "سبب التنقيص أو الحذف" : "Reduction or removal reason"}
              value={managerReductionReason}
              onChange={(event) => setManagerReductionReason(event.target.value)}
            />
            {managerReductionError ? <Alert tone="danger" title={ar ? "تعذر التفويض" : "Authorization failed"}>{managerReductionError}</Alert> : null}
          </Stack>
        ) : null}
      </Dialog>

      <Dialog open={Boolean(configProduct)} onClose={closeConfigurator} title={configProduct ? (ar ? configProduct.nameAr : configProduct.nameEn) : ""} footer={<Inline><Button variant="secondary" onClick={closeConfigurator}>{ar ? "إلغاء" : "Cancel"}</Button><Button data-testid={editingLineId ? "save-order-line-amendment" : "add-configured-product"} disabled={!configVariantId || isOrderItemEditingLocked} onClick={() => void saveConfiguredProduct()}>{editingLineId ? (ar ? "حفظ التعديل" : "Save changes") : (ar ? "إضافة للطلب" : "Add to order")}</Button></Inline>}>
        {configProduct ? <Stack>
          {editingLineId && activePreOrder?.summary.kitchenReleaseState === "Released" ? (
            <Alert tone="info" title={ar ? "تحديث مباشر للمطبخ" : "Live kitchen amendment"}>
              {ar
                ? "عند الحفظ، تُلغى نسخة الصنف السابقة في KDS وتُرسل تذكرة تعديل جديدة بالكمية والإضافات والملاحظة المحدثة."
                : "Saving cancels the previous KDS item and sends a new amendment ticket with the updated quantity, modifiers and note."}
            </Alert>
          ) : null}
          <div className="cashier-choice-block cashier-variant-choice"><span className="cashier-choice-label">{ar ? "المتغير" : "Variant"}</span><div className="cashier-choice-compat-select-wrap" aria-hidden="true"><SelectField label={ar ? "المتغير" : "Variant"} value={configVariantId} disabled={Boolean(editingLineId)} onChange={(event) => setConfigVariantId(event.target.value)}>{configProduct.variants.map((variant) => <option key={variant.id} value={variant.id}>{variantOptionLabel(variant)}</option>)}</SelectField></div><div className="cashier-choice-grid cashier-variant-grid">{configProduct.variants.map((variant) => <Button key={variant.id} type="button" size="sm" variant={configVariantId === variant.id ? "primary" : "secondary"} disabled={Boolean(editingLineId)} onClick={() => setConfigVariantId(variant.id)}>{variantOptionLabel(variant)}</Button>)}</div></div><Field label={ar ? "الكمية" : "Quantity"} type="number" min={Math.max(1, configProduct.quantityStepMilli) / 1000} step={Math.max(1, configProduct.quantityStepMilli) / 1000} value={configQuantity} onChange={(event) => setConfigQuantity(event.target.value)} /><TextAreaField label={ar ? "ملاحظة الصنف" : "Item note"} value={configNote} onChange={(event) => setConfigNote(event.target.value)} />{configProduct.modifierGroups.map((group) => <Card key={group.id} title={ar ? group.nameAr : group.nameEn}><div className="cashier-modifier-options">{group.options.map((option) => <Checkbox key={option.id} label={<span className="cashier-modifier-option-label"><span>{ar ? option.nameAr : option.nameEn}</span><strong>{option.priceDeltaMinor === 0 ? (ar ? "بدون زيادة" : "No extra") : `${option.priceDeltaMinor > 0 ? "+" : "−"}${formatMoney(Math.abs(option.priceDeltaMinor), catalog?.currency)}`}</strong></span>} checked={configOptionIds.includes(option.id)} onChange={(event) => setConfigOptionIds((current) => event.target.checked ? [...current.filter((id) => id !== option.id), option.id] : current.filter((id) => id !== option.id))} />)}</div></Card>)}</Stack> : null}
      </Dialog>

      <CashierPaymentDialog
  open={paymentOpen}
  language={language}
  workflow={workflow}
  runtimeSettings={runtimeSettings}
  customers={customers}
  online={offline.online}
  invoiceSection={
    <Card title={ar ? "الفاتورة الضريبية" : "Tax invoice"}>
      <Stack>
        <Checkbox
          data-testid="payment-tax-invoice-requested"
          label={ar ? "إصدار فاتورة ضريبية" : "Issue tax invoice"}
          checked={contextTaxInvoiceRequested}
          onChange={(event) => setContextTaxInvoiceRequested(event.target.checked)}
        />
        {contextTaxInvoiceRequested ? (
          <>
            <Field
              data-testid="payment-tax-invoice-company"
              label={ar ? "اسم الشركة" : "Company legal name"}
              value={contextTaxInvoiceCompanyName}
              onChange={(event) => setContextTaxInvoiceCompanyName(event.target.value)}
            />
            <Field
              data-testid="payment-tax-invoice-trn"
              label="TRN"
              value={contextTaxInvoiceTrn}
              onChange={(event) => setContextTaxInvoiceTrn(event.target.value)}
            />
            <TextAreaField
              data-testid="payment-tax-invoice-address"
              label={ar ? "عنوان الشركة" : "Company address"}
              value={contextTaxInvoiceAddress}
              onChange={(event) => setContextTaxInvoiceAddress(event.target.value)}
            />
          </>
        ) : null}
      </Stack>
    </Card>
  }
  formatMoney={formatMoney}
  onClose={() => setPaymentOpen(false)}
  onCaptureSingle={captureSinglePayment}
  onCaptureSplit={captureSplitPayment}
  onError={setError}
/>

      <Dialog
        open={Boolean(receipt)}
        onClose={() => { setReceipt(null); setReceiptWorkflow(null); }}
        title={receipt ? receiptDocumentTitle(receipt) : ar ? "الإيصال" : "Receipt"}
        footer={
          <Inline>
            <Button data-testid="close-payment-receipt" variant="secondary" onClick={() => { setReceipt(null); setReceiptWorkflow(null); }}>{ar ? "إغلاق" : "Close"}</Button>
            {receipt && receipt.receiptType === "Sale"
              && (receipt.fiscalDocumentType === "StandardTaxInvoice"
                || receipt.fiscalDocumentType === "SimplifiedTaxInvoice") ? (
              <Button variant="secondary" onClick={() => printReceipt(receipt, "fiscal")}>
                {ar ? "طباعة الفاتورة الضريبية" : "Print tax invoice"}
              </Button>
            ) : null}
            {receipt ? (
              <Button data-testid="print-payment-receipt" onClick={() => printReceipt(receipt, "payment")}>
                {ar ? `طباعة ${receiptDocumentTitle(receipt)}` : `Print ${receiptDocumentTitle(receipt)}`}
              </Button>
            ) : null}
          </Inline>
        }
      >
        {receipt ? (
          <Stack>
            {receipt.receiptType === "Sale" && receiptWorkflow ? (
              <Card title={ar ? "فاتورة ضريبية لعميل محفوظ" : "Tax invoice for a saved customer"}>
                <Stack>
                  <CashierCustomerSearchPicker
                    language={language}
                    label={ar ? "اختر العميل لتعبئة بيانات الفاتورة" : "Choose customer to fill invoice details"}
                    value=""
                    initialCustomers={customers}
                    selectedCustomer={null}
                    onSelect={(customer) => {
                      if (!customer || !receiptWorkflow) return;
                      setWorkflow(receiptWorkflow);
                      hydrateContext(receiptWorkflow);
                      selectContextCustomerRecord(customer);
                      setContextTaxInvoiceRequested(true);
                      setPaidCustomerCorrectionReason(ar
                        ? "إعادة إصدار الفاتورة الضريبية باستخدام بيانات عميل محفوظ"
                        : "Reissue tax invoice using saved customer identity");
                      setReceipt(null);
                      setPaidCustomerCorrectionOpen(true);
                    }}
                    emptyLabel={ar ? "اختر عميلاً…" : "Choose customer…"}
                    searchTestId="receipt-customer-search"
                    selectTestId="receipt-saved-customer"
                    enabled={can("customers.read")}
                  />
                  <small>{ar ? "يتم تعبئة الاسم والهاتف والشركة والرقم الضريبي والعنوان تلقائياً، ثم يمكنك مراجعتها قبل إعادة الإصدار." : "Name, phone, company, TRN and address are filled automatically and remain reviewable before reissue."}</small>
                </Stack>
              </Card>
            ) : null}
            <strong>{receipt.receiptNumber}</strong>
            <span>{displayOrderNumber(receipt.orderNumber)}</span>
            <span>{formatMoney(receipt.totalMinor, receipt.currency)}</span>
            {receipt.payments.some(payment => payment.method === "GiftCard") ? (
              <div className="identity-list" data-testid="receipt-payment-summary">
                {receipt.payments.map((payment, index) => (
                  <div className="identity-row" key={index} data-payment-method={payment.method} data-amount-minor={payment.amountMinor}>
                    <span>{paymentMethodLabel(payment.method)}</span>
                    <bdi dir="ltr">{formatMoney(payment.amountMinor, receipt.currency)}</bdi>
                  </div>
                ))}
                <div className="identity-row">
                  <span>{ar ? "إجمالي المدفوع" : "Total paid"}</span>
                  <bdi dir="ltr">{formatMoney(receipt.payments.reduce((total, payment) => total + payment.amountMinor, 0), receipt.currency)}</bdi>
                </div>
              </div>
            ) : null}
            <div className="receipt-preview__discounts" data-testid="receipt-discount-summary">
              <div className="identity-list">
                {receiptDiscountSummary(receipt, ar).rows.map(row => (
                  <div className="identity-row" data-discount={row.key} data-amount-minor={row.amountMinor} key={row.key}
                    style={{ display: "flex", justifyContent: "space-between", gap: "1rem", flexWrap: "wrap" }}>
                    <span>{row.label}</span>
                    <bdi dir="ltr">{formatMoney(-row.amountMinor, receipt.currency)}</bdi>
                  </div>
                ))}
              </div>
            </div>
            <div className="receipt-preview__recipient">
              <strong>{ar ? "بيانات العميل / الشركة" : "Customer / company details"}</strong>
              <div className="identity-list">
                <div className="identity-row">
                  <span>{ar ? "اسم العميل / الشركة" : "Customer / company name"}</span>
                  <b>{receipt.customerCompanyName ?? receipt.customerName ?? "—"}</b>
                </div>
                <div className="identity-row">
                  <span>{ar ? "الموقع / العنوان" : "Location / address"}</span>
                  <b>{receipt.customerCompanyAddress ?? "—"}</b>
                </div>
                <div className="identity-row">
                  <span>{ar ? "رقم الهاتف" : "Phone"}</span>
                  <bdi dir="ltr">{receipt.customerPhone ?? "—"}</bdi>
                </div>
                <div className="identity-row">
                  <span>{ar ? "الرقم الضريبي" : "TRN"}</span>
                  <bdi dir="ltr">{receipt.customerTaxRegistrationNumber ?? "—"}</bdi>
                </div>
              </div>
            </div>
            {receipt.revisionNumber > 1 ? (
              <Alert tone="info" title={ar ? "مستند معاد الإصدار" : "Reissued document"}>
                <strong data-testid="receipt-revision">
                  {ar ? "إعادة إصدار" : "Reissued revision"} #{receipt.revisionNumber}
                </strong>
                {receipt.reissueReason ? <span>{receipt.reissueReason}</span> : null}
              </Alert>
            ) : null}
          </Stack>
        ) : null}
      </Dialog>

      <Dialog open={holdOpen} onClose={() => setHoldOpen(false)} title={ar ? "تعليق الطلب" : "Hold order"} footer={<Inline><Button variant="secondary" onClick={() => setHoldOpen(false)}>{ar ? "إلغاء" : "Cancel"}</Button><Button onClick={() => void holdOrder()}>{ar ? "تعليق" : "Hold"}</Button></Inline>}><TextAreaField label={ar ? "السبب" : "Reason"} value={holdReason} onChange={(event) => setHoldReason(event.target.value)} /></Dialog>

      <Dialog open={discountOpen} onClose={() => setDiscountOpen(false)} title={ar ? "خصم" : "Discount"} footer={<Inline><Button variant="secondary" onClick={() => setDiscountOpen(false)}>{ar ? "إلغاء" : "Cancel"}</Button><Button disabled={!discountReason.trim()} onClick={() => void applyDiscount()}>{ar ? "تطبيق" : "Apply"}</Button></Inline>}><Stack><Field label={ar ? "النسبة %" : "Percentage %"} type="number" min={0} value={discountValue} onChange={(event) => setDiscountValue(event.target.value)} /><TextAreaField label={ar ? "سبب الخصم" : "Discount reason"} required value={discountReason} onChange={(event) => setDiscountReason(event.target.value)} /></Stack></Dialog>

      <Dialog open={couponOpen} onClose={() => { if (!couponBusy) setCouponOpen(false); }} title={ar ? "قسيمة" : "Coupon"} footer={<Inline><Button variant="secondary" disabled={couponBusy} onClick={() => setCouponOpen(false)}>{ar ? "إلغاء" : "Cancel"}</Button><Button data-testid="apply-coupon" disabled={couponBusy || paidPreOrderCoupon || !couponCode.trim() || (couponNeedsCustomer && !workflow?.order.customerId && (!couponCustomer || !canSelectCouponCustomer))} onClick={() => void applyCoupon()}>{couponBusy ? (ar ? "جارٍ التطبيق…" : "Applying…") : (ar ? "تطبيق" : "Apply")}</Button></Inline>}>
        <Stack>
          {couponError ? <Alert tone="danger" title={ar ? "تعذر تطبيق القسيمة" : "Coupon could not be applied"}><span data-testid="coupon-error" role="alert">{couponError}</span></Alert> : null}
          {workflow?.appliedCoupon ? <Inline><strong>{ar ? "القسيمة المطبقة" : "Applied coupon"}: <bdi>{workflow.appliedCoupon.code}</bdi></strong><Button data-testid="remove-coupon" variant="secondary" disabled={couponBusy || paidPreOrderCoupon || workflow.order.amountPaidMinor !== 0} onClick={() => void removeCoupon()}>{ar ? "إزالة القسيمة" : "Remove coupon"}</Button></Inline> : null}
          {paidPreOrderCoupon ? <Alert tone="info" title={ar ? "مراجعة المدير مطلوبة" : "Manager review required"}>
            {ar ? "بعد تسجيل دفعة، تُزال القسيمة من مراجعة التسعير لدى المدير، ثم يمكن تطبيق قسيمة أخرى." : "After a payment, a manager must remove the coupon through pricing review before another coupon is applied."}
            {canReviewPricing ? <Button data-testid="coupon-pricing-recovery" variant="secondary" disabled={couponBusy} onClick={() => { setCouponOpen(false); setPricingRecoveryOrderId(workflow!.order.id); }}>{ar ? "مراجعة التسعير" : "Review pricing"}</Button> : null}
          </Alert> : null}
          {workflow && couponOpen ? <CashierCouponPicker orderId={workflow.order.id} orderVersion={workflow.order.version} language={language} disabled={couponBusy || paidPreOrderCoupon} onSelect={(code, requiresCustomer) => { setCouponCode(code); setCouponNeedsCustomer(requiresCustomer); setCouponCustomer(null); setCouponError(""); }} /> : null}
          <Field dir="ltr" data-testid="coupon-code" label={ar ? "رمز القسيمة" : "Coupon code"} value={couponCode} disabled={couponBusy || paidPreOrderCoupon} onChange={(event) => { setCouponCode(event.target.value.toUpperCase()); setCouponNeedsCustomer(false); setCouponCustomer(null); setCouponError(""); }} />
          {couponNeedsCustomer && !workflow?.order.customerId ? <Stack>
            <Alert tone="info" title={ar ? "هذه القسيمة تحتاج عميلًا مسجلًا" : "This coupon requires a registered customer"}>
              {couponCustomerLocked ? (ar ? "لا يمكن ربط عميل بطلب مستقبلي بعد تسجيل دفعة. استخدم قسيمة عامة مؤهلة." : "A customer cannot be attached to a future order after a payment. Use an eligible public coupon.")
                : !canSelectCouponCustomer ? (ar ? "اختيار العميل يحتاج صلاحية قراءة العملاء وتعديل الطلب. اطلب مساعدة مستخدم مخوّل." : "Customer selection requires customer-read and order-update permissions. Ask an authorized user.")
                : (ar ? "اختر العميل ثم أكد التطبيق. يبقى الرمز محفوظًا، ولا تُمنح النقاط أو رصيد المحفظة قبل اكتمال البيع واستيفاء الشروط." : "Choose the customer and confirm application. The code is retained; points or wallet credit are posted only when the sale completes and conditions are met.")}
            </Alert>
            {canSelectCouponCustomer ? <CashierCustomerSearchPicker language={language} label={ar ? "عميل القسيمة" : "Coupon customer"}
              value={couponCustomer?.id ?? ""} initialCustomers={customers} selectedCustomer={couponCustomer} onSelect={setCouponCustomer}
              emptyLabel={ar ? "اختر عميلًا" : "Select a customer"} searchTestId="coupon-customer-search" selectTestId="coupon-customer"
              enabled={!couponBusy} /> : null}
          </Stack> : null}
        </Stack>
      </Dialog>

      <Dialog
        open={cancelOpen}
        onClose={() => { if (!cancelBusyRef.current) setCancelOpen(false); }}
        title={workflow?.order.status === "Ready"
          ? (ar ? "إلغاء بموافقة المدير" : "Manager-approved cancellation")
          : (ar ? "إلغاء الطلب" : "Cancel order")}
        footer={
          <Inline>
            <Button variant="secondary" disabled={cancelBusy} onClick={() => setCancelOpen(false)}>{ar ? "رجوع" : "Back"}</Button>
            <Button data-testid="confirm-order-cancellation" variant="danger" disabled={cancelBusy || !cancelReason.trim()} onClick={() => void cancelOrder()}>
              {workflow?.order.status === "Ready"
                ? (ar ? "إرسال للمدير" : "Send to manager")
                : (ar ? "تأكيد الإلغاء" : "Confirm cancel")}
            </Button>
          </Inline>
        }
      >
        {cancelError ? <Alert tone="danger" title={ar ? "تعذر إتمام الإجراء" : "Action could not be completed"}><span role="alert">{cancelError}</span></Alert> : null}
        <TextAreaField
          label={workflow?.order.status === "Ready"
            ? (ar ? "سبب طلب الإلغاء" : "Cancellation approval reason")
            : (ar ? "سبب الإلغاء" : "Cancellation reason")}
          required
          disabled={cancelBusy}
          value={cancelReason}
          onChange={(event) => setCancelReason(event.target.value)}
        />
      </Dialog>

      <GiftCardPaymentDialog open={giftPaymentOpen} language={language} workflow={workflow} preOrder={activePreOrder} permissions={permissions}
        onClose={() => setGiftPaymentOpen(false)} onPrepare={async () => {
          if (!workflow) throw new Error("Order is required");
          if (activePreOrder || workflow.payments.length) return workflow;
          const prepared = await freezePaymentContext(workflow, workflow.customer?.id ?? (contextCustomerId || null));
          setWorkflow(prepared); hydrateContext(prepared); return prepared;
        }} onCaptured={async (id, issued) => { await loadWorkflow(id); if (issued) { const updated = await getOrderWorkflow(id); setReceiptWorkflow(updated); setReceipt(issued); } await refreshReferenceData(); }} />
      {lastRefundDocument && lastRefundDocument.orderId === workflow?.order.id && !refunds.some(x => x.id === lastRefundDocument.refundId) ? <RefundDocumentPanel key={lastRefundDocument.refundId} {...lastRefundDocument} language={language} /> : null}
      {workflow ? refunds.filter(refund => refund.status === "Completed").map(refund =>
        <RefundDocumentPanel key={`document-${refund.id}`} orderId={workflow.order.id} refundId={refund.id} refundNumber={refund.refundNumber} language={language} />) : null}
      {lastGiftRefund ? <RefundGiftCardPrintPanel key={lastGiftRefund.refundId} {...lastGiftRefund} language={language} /> : null}
      <Dialog open={refundOpen} onClose={() => setRefundOpen(false)} title={ar ? "استرداد" : "Refund"} footer={<Inline><Button variant="secondary" onClick={() => setRefundOpen(false)}>{ar ? "إلغاء" : "Cancel"}</Button><Button data-testid="process-refund" disabled={refundBusy || (!refundPending && !allocationPreview.preview) || (giftReplacementRequired && !can("giftcards.manage")) || !refundReason.trim() || !Object.values(refundQuantities).some((value) => value > 0)} onClick={() => void processRefund()}>{giftReplacementRequired ? (ar ? "إصدار بطاقة بديلة وإتمام الاسترداد" : "Issue replacement and complete refund") : runtimeSettings?.refundPolicy === "ManagerApproval" && !can("refunds.approve") ? (ar ? "إرسال للمدير" : "Send to manager") : (ar ? "تنفيذ الاسترداد" : "Process refund")}</Button></Inline>}>
        <RefundAllocationPreview {...allocationPreview} retry={allocationPreview.refresh} language={language} />
        {giftReplacementRequired ? <GiftCardReplacementFields language={language} permitted={can("giftcards.manage")} noExpiry={giftNoExpiry} expiry={giftExpiry} onNoExpiry={setGiftNoExpiry} onExpiry={setGiftExpiry} disabled={refundBusy || Boolean(refundPending)} /> : null}
        {refundPending ? <Alert tone="info" title={ar ? "أعد المحاولة لاستعادة نتيجة الاسترداد نفسه" : "Retry to recover the same refund result"} /> : null}
        {refunds.filter(r => r.status === "Completed" && r.paymentAllocations.some(p => p.method === "GiftCard")).map(r => <RefundGiftCardPrintPanel key={r.id} orderId={r.orderId} refundId={r.id} language={language} />)}
        <Stack><TextAreaField data-testid="refund-reason" label={ar ? "سبب الاسترداد" : "Refund reason"} value={refundReason} onChange={(event) => setRefundReason(event.target.value)} />{refundableLines.map((line) => { const selected = (refundQuantities[line.id] ?? 0) > 0; return <div className="refund-line" key={line.id}><Checkbox data-testid={`refund-line-select-${line.id}`} label={line.name} checked={selected} onChange={(event) => setRefundQuantities((current) => ({ ...current, [line.id]: event.target.checked ? line.availableQuantityMilli : 0 }))} /><Field data-testid={`refund-quantity-${line.id}`} label={ar ? "كمية الاسترداد" : "Refund quantity"} type="number" min={0.001} max={line.availableQuantityMilli / 1000} step={0.001} disabled={!selected} value={selected ? (refundQuantities[line.id] ?? 0) / 1000 : ""} onChange={(event) => setRefundQuantities((current) => ({ ...current, [line.id]: Math.round(Number(event.target.value) * 1000) }))} /></div>; })}</Stack>
      </Dialog>
    </>
  );
}
